// ═══════════════════════════════════════════════════════════════════════
// ADVISIQ DSS — IAEL App.js  v10.0  Sprint F3
// Surface 1: Regime Intelligence Terminal — COMPLETE
// Surface 2: Advisory Brief Workspace — COMPLETE
// Surface 3: Observability Console — COMPLETE
// Narrative Orchestration Layer — COMPLETE
// Version: 10.1.0
// Date: 2026-05-24
// ═══════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, useCallback } from "react";

// ═══════════════════════════════════════════════════════════════════════
// S0-D1 — INSTITUTIONAL THEME SYSTEM
// ═══════════════════════════════════════════════════════════════════════
const THEME = {
  bg:           "#0a0d10",
  surfPrimary:  "#0f1318",
  surfSec:      "#0c1016",
  surfTert:     "#111820",
  border:       "#1c2730",
  borderSub:    "#172028",
  textPrimary:  "#e8edf2",
  textSecondary:"#8fa5b8",
  textTertiary: "#4d6478",
  textMuted:    "#334455",
  regime: {
    RISK_ON:         "#3a6b8a",
    STRONG_RISK_ON:  "#3a6b8a",
    RISK_OFF:        "#7a6030",
    STRONG_RISK_OFF: "#7a6030",
    NEUTRAL:         "#3d4f5e",
    CAUTION:         "#5c6e42",
    UNKNOWN:         "#334455",
  },
  affordance:      "#4a7fa5",
  affordanceHover: "#5a92bb",
  governance:      "#8a6d30",
  governanceBg:    "rgba(138,109,48,0.08)",
  governanceBorder:"rgba(138,109,48,0.25)",
  isolation:       "#2a4d6e",
  isolationBg:     "rgba(42,77,110,0.10)",
  isolationBorder: "rgba(42,77,110,0.30)",
  fontMono: "'JetBrains Mono','Courier New',monospace",
  fontSans: "'Inter','Helvetica Neue',sans-serif",
};

// ═══════════════════════════════════════════════════════════════════════
// S0-D2 — SESSION STATE MACHINE
// ═══════════════════════════════════════════════════════════════════════
export const SESSION_STATES = Object.freeze({
  AMBIENT:          "AMBIENT",
  CONTEXT_ENTRY:    "CONTEXT_ENTRY",
  PROCESSING:       "PROCESSING",
  POSTURE_ACTIVE:   "POSTURE_ACTIVE",
  TRACE_ACTIVE:     "TRACE_ACTIVE",
  SESSION_CLEARING: "SESSION_CLEARING",
  CLEARED:          "CLEARED",
  WARMUP:           "WARMUP",
});

const VALID_TRANSITIONS = {
  [SESSION_STATES.AMBIENT]:          [SESSION_STATES.CONTEXT_ENTRY, SESSION_STATES.WARMUP],
  [SESSION_STATES.CONTEXT_ENTRY]:    [SESSION_STATES.PROCESSING, SESSION_STATES.AMBIENT],
  [SESSION_STATES.PROCESSING]:       [SESSION_STATES.POSTURE_ACTIVE, SESSION_STATES.CONTEXT_ENTRY],
  [SESSION_STATES.POSTURE_ACTIVE]:   [SESSION_STATES.TRACE_ACTIVE, SESSION_STATES.SESSION_CLEARING, SESSION_STATES.PROCESSING],
  [SESSION_STATES.TRACE_ACTIVE]:     [SESSION_STATES.POSTURE_ACTIVE, SESSION_STATES.SESSION_CLEARING],
  [SESSION_STATES.SESSION_CLEARING]: [SESSION_STATES.CLEARED, SESSION_STATES.POSTURE_ACTIVE],
  [SESSION_STATES.CLEARED]:          [SESSION_STATES.AMBIENT],
  [SESSION_STATES.WARMUP]:           [SESSION_STATES.AMBIENT],
};

export function isValidTransition(from, to) {
  return Array.isArray(VALID_TRANSITIONS[from]) && VALID_TRANSITIONS[from].includes(to);
}

function createSessionState() {
  return {
    state: SESSION_STATES.AMBIENT,
    portfolioContext: null,
    briefResult: null,
    warmupActive: false,
    sessionId: "s_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2,8),
    enteredAt: null,
    clearedAt: null,
  };
}

function _obsLog(event) {
  try { console.log("[IAEL-OBS]", JSON.stringify(event)); } catch(e) {}
}

export function useSessionMachine() {
  const [session, setSession] = useState(createSessionState);
  const transition = useCallback((toState, payload) => {
    setSession(prev => {
      if (!isValidTransition(prev.state, toState)) {
        _obsLog({ eventType:"PROHIBITED_TRANSITION_ATTEMPTED", sessionId:prev.sessionId, from:prev.state, to:toState, timestamp:Date.now() });
        return prev;
      }
      _obsLog({ eventType:"STATE_TRANSITION", sessionId:prev.sessionId, from:prev.state, to:toState, timestamp:Date.now() });
      switch(toState) {
        case SESSION_STATES.CONTEXT_ENTRY:
          if (prev.state === SESSION_STATES.AMBIENT)
            _obsLog({ eventType:"PORTFOLIO_CONTEXT_ENTERED", sessionId:prev.sessionId, timestamp:Date.now() });
          return { ...prev, state:toState, portfolioContext:payload||prev.portfolioContext, enteredAt:prev.enteredAt||Date.now() };
        case SESSION_STATES.PROCESSING:
          return { ...prev, state:toState, briefResult:null };
        case SESSION_STATES.POSTURE_ACTIVE:
          return { ...prev, state:toState, briefResult:payload||prev.briefResult };
        case SESSION_STATES.TRACE_ACTIVE:
          return { ...prev, state:toState };
        case SESSION_STATES.SESSION_CLEARING:
          _obsLog({ eventType:"SESSION_CLEARING_INITIATED", sessionId:prev.sessionId, timestamp:Date.now() });
          return { ...prev, state:toState };
        case SESSION_STATES.CLEARED:
          _obsLog({ eventType:"SESSION_CLEARED", sessionId:prev.sessionId, timestamp:Date.now() });
          return { ...createSessionState(), clearedAt:Date.now() };
        case SESSION_STATES.AMBIENT:
          return { ...prev, state:toState };
        case SESSION_STATES.WARMUP:
          return { ...prev, state:toState, warmupActive:true };
        default: return prev;
      }
    });
  }, []);
  return [session, transition];
}

// ═══════════════════════════════════════════════════════════════════════
// S0-D4 — ABSTRACTION MAPPING V1
// ═══════════════════════════════════════════════════════════════════════
export const ABSTRACTION_MAP_VERSION = "V2";

// ── AUDIENCE TRANSLATION TABLE (P4) ──────────────────────────────────
// Maps canonical DSS states to audience-specific display labels.
// Surface 1: no audience param → Universal column
// Surface 2: audience param passed → audience-specific label
// Surface 3: no audience param → institutional (same as no-param)
// Canonical backend states NEVER change. This is presentation only.
const AUDIENCE_KEYS = {
  "IFA Advisory Pitch":         "IFA",
  "Wealth Manager Synthesis":   "WM",
  "Family Office Risk Brief":   "FO",
  "Private Banker Advisory":    "PB",
  "Advanced Investor Dashboard":"ADV",
  "Retail Investor":            "RETAIL",
  "HNI Investor":               "HNI",
  "Conservative Retiree":       "RETIREE",
  "Aggressive Growth Investor": "AGG",
  "Institutional Strategy Desk":"INST",
};

const REGIME_TRANSLATIONS = {
  RISK_ON:         { IFA:"Constructive Market", WM:"Constructive Environment", FO:"Constructive Market Conditions", PB:"Constructive Market", ADV:"RISK-ON", RETAIL:"Positive Market", HNI:"Positive Market Environment", RETIREE:"Cautiously Positive", AGG:"Risk-On", INST:"RISK_ON" },
  STRONG_RISK_ON:  { IFA:"Strongly Constructive", WM:"Strongly Constructive", FO:"Strong Constructive Conditions", PB:"Strongly Constructive", ADV:"STRONG RISK-ON", RETAIL:"Strong Positive Market", HNI:"Strong Positive Market", RETIREE:"Moderately Positive", AGG:"Strong Risk-On", INST:"STRONG_RISK_ON" },
  NEUTRAL:         { IFA:"Balanced Market", WM:"Balanced Environment", FO:"Balanced Market Conditions", PB:"Balanced Market", ADV:"NEUTRAL", RETAIL:"Mixed Market", HNI:"Balanced Market", RETIREE:"Stable — No Strong Signal", AGG:"Neutral", INST:"NEUTRAL" },
  RISK_OFF:        { IFA:"Defensive Market", WM:"Defensive Environment", FO:"Defensive Market Conditions", PB:"Defensive Market", ADV:"RISK-OFF", RETAIL:"Cautious Market", HNI:"Cautious Market Environment", RETIREE:"Protective Positioning Warranted", AGG:"Risk-Off", INST:"RISK_OFF" },
  STRONG_RISK_OFF: { IFA:"Strongly Defensive", WM:"Strongly Defensive", FO:"Strong Defensive Conditions", PB:"Strongly Defensive", ADV:"STRONG RISK-OFF", RETAIL:"Highly Cautious Market", HNI:"Highly Cautious Market", RETIREE:"Strong Protective Positioning", AGG:"Strong Risk-Off", INST:"STRONG_RISK_OFF" },
};

const DURABILITY_TRANSLATIONS = {
  EMERGING:   { IFA:"Early Stage", WM:"Early Stage", FO:"New Regime — Early Confirmation", PB:"Early Stage", ADV:"EMERGING", RETAIL:"New Direction", HNI:"New Direction — Unconfirmed", RETIREE:"Too Early to Confirm", AGG:"Early Stage", INST:"EMERGING" },
  CONFIRMING: { IFA:"Building Confirmation", WM:"Gaining Confirmation", FO:"Building Evidence", PB:"Gaining Confirmation", ADV:"CONFIRMING", RETAIL:"Direction Strengthening", HNI:"Direction Strengthening", RETIREE:"Pattern Developing", AGG:"Confirming", INST:"CONFIRMING" },
  ESTABLISHED:{ IFA:"Well Established", WM:"Well Established", FO:"Established Pattern", PB:"Well Established", ADV:"ESTABLISHED", RETAIL:"Established Direction", HNI:"Established Direction", RETIREE:"Confirmed Pattern", AGG:"Established", INST:"ESTABLISHED" },
  MATURE:     { IFA:"Long-Standing", WM:"Long-Standing", FO:"Extended Regime", PB:"Long-Standing", ADV:"MATURE", RETAIL:"Sustained Direction", HNI:"Sustained Direction", RETIREE:"Long-Standing Pattern", AGG:"Mature", INST:"MATURE" },
};

const INTENSITY_TRANSLATIONS = {
  OBSERVE:             { IFA:"Monitor — No Action", WM:"Monitor — No Action", FO:"Observe and Monitor", PB:"Monitor", ADV:"OBSERVE", RETAIL:"Watch and Wait", HNI:"Watch and Wait", RETIREE:"No Action — Monitor", AGG:"Observe", INST:"OBSERVE" },
  CONSIDER:            { IFA:"Consider Positioning", WM:"Consider Adjusting", FO:"Consider Positioning", PB:"Consider Adjusting", ADV:"CONSIDER", RETAIL:"Consider Small Changes", HNI:"Consider Adjusting", RETIREE:"Consider Small Steps", AGG:"Consider", INST:"CONSIDER" },
  ACT:                 { IFA:"Act on Positioning", WM:"Act on Positioning", FO:"Act on Positioning", PB:"Act", ADV:"ACT", RETAIL:"Make Recommended Changes", HNI:"Act on Positioning", RETIREE:"Take Considered Action", AGG:"Act", INST:"ACT" },
  MAINTAIN:            { IFA:"Maintain Current Approach", WM:"Maintain Allocation", FO:"Maintain Current Positioning", PB:"Maintain", ADV:"MAINTAIN", RETAIL:"Stay the Course", HNI:"Stay the Course", RETIREE:"Stay the Course", AGG:"Maintain", INST:"MAINTAIN" },
  PRIORITISE_REDUCTION:{ IFA:"Reduce Equity Exposure", WM:"Reduce Equity Allocation", FO:"Reduce Growth Exposure", PB:"Prioritise Reduction", ADV:"PRIORITISE REDUCTION", RETAIL:"Reduce Equity", HNI:"Reduce Equity Exposure", RETIREE:"Reduce Risk — Protect Capital", AGG:"Reduce Exposure", INST:"PRIORITISE_REDUCTION" },
};

const MISMATCH_TRANSLATIONS = {
  MINIMAL:      { IFA:"Portfolio Aligned", WM:"Allocation Aligned", FO:"Portfolio Aligned", PB:"Aligned", ADV:"MINIMAL", RETAIL:"On Track", HNI:"Allocation Aligned", RETIREE:"On Track", AGG:"Aligned", INST:"MINIMAL" },
  MODERATE:     { IFA:"Minor Adjustment Suggested", WM:"Minor Rebalancing Suggested", FO:"Minor Adjustment", PB:"Minor Adjustment", ADV:"MODERATE MISMATCH", RETAIL:"Minor Adjustment Needed", HNI:"Minor Adjustment", RETIREE:"Small Adjustment Suggested", AGG:"Moderate Mismatch", INST:"MODERATE" },
  SIGNIFICANT:  { IFA:"Rebalancing Recommended", WM:"Rebalancing Recommended", FO:"Adjustment Recommended", PB:"Moderate Adjustment", ADV:"SIGNIFICANT MISMATCH", RETAIL:"Adjustment Recommended", HNI:"Rebalancing Recommended", RETIREE:"Adjustment Recommended", AGG:"Significant Mismatch", INST:"SIGNIFICANT" },
  SUBSTANTIAL:  { IFA:"Significant Rebalancing Needed", WM:"Significant Rebalancing", FO:"Significant Adjustment", PB:"Significant Adjustment", ADV:"SUBSTANTIAL MISMATCH", RETAIL:"Significant Changes Needed", HNI:"Significant Rebalancing", RETIREE:"Important Adjustment Needed", AGG:"Substantial Mismatch", INST:"SUBSTANTIAL" },
};

const DIRECTION_TRANSLATIONS = {
  OVERWEIGHT: { IFA:"Above Recommended Range", WM:"Above Recommended Allocation", FO:"Above Recommended Range", PB:"Overweight", ADV:"OVERWEIGHT", RETAIL:"Higher than Recommended", HNI:"Above Recommended Range", RETIREE:"Higher than Suitable", AGG:"Overweight", INST:"OVERWEIGHT" },
  UNDERWEIGHT:{ IFA:"Below Recommended Range", WM:"Below Recommended Allocation", FO:"Below Recommended Range", PB:"Underweight", ADV:"UNDERWEIGHT", RETAIL:"Lower than Recommended", HNI:"Below Recommended Range", RETIREE:"Lower than Suitable", AGG:"Underweight", INST:"UNDERWEIGHT" },
  ALIGNED:    { IFA:"Within Recommended Range", WM:"Within Recommended Allocation", FO:"Within Recommended Range", PB:"Aligned", ADV:"ALIGNED", RETAIL:"At Recommended Level", HNI:"Within Recommended Range", RETIREE:"At Suitable Level", AGG:"Aligned", INST:"ALIGNED" },
};

function _audienceKey(audience) {
  return AUDIENCE_KEYS[audience] || null;
}
export function mapDirection(d, audience) {
  const ak = _audienceKey(audience);
  const universalLabels = { OVERWEIGHT:"Overweight", UNDERWEIGHT:"Underweight", ALIGNED:"Aligned", NONE:"—" };
  const label = ak && DIRECTION_TRANSLATIONS[d]?.[ak] ? DIRECTION_TRANSLATIONS[d][ak] : (universalLabels[d] || d || "—");
  return { label };
}
// ── END AUDIENCE TRANSLATION TABLE ───────────────────────────────────


export function mapConvictionClass(c) {
  return ({ STRONG:{label:"STRONG",desc:"Strong advisory conviction"}, HIGH:{label:"HIGH",desc:"High advisory conviction"}, MODERATE:{label:"MODERATE",desc:"Moderate advisory conviction"}, LOW:{label:"LOW",desc:"Low advisory conviction"} })[c] || { label:"—", desc:"Conviction unavailable" };
}
export function mapDurabilityClass(c, audience) {
  const ak = _audienceKey(audience);
  const descs = { EMERGING:"Regime emerging — early cycle", CONFIRMING:"Regime confirming — building stability", ESTABLISHED:"Regime established — sustained pattern", MATURE:"Regime mature — extended cycle" };
  const universalLabels = { EMERGING:"Early Stage", CONFIRMING:"Building Confirmation", ESTABLISHED:"Established", MATURE:"Mature" };
  const label = ak && DURABILITY_TRANSLATIONS[c]?.[ak] ? DURABILITY_TRANSLATIONS[c][ak] : (universalLabels[c] || "—");
  return { label, desc: descs[c] || "Durability unavailable" };
}
export function mapRegime(r, audience) {
  const ak = _audienceKey(audience);
  const colors = { RISK_ON:THEME.regime.RISK_ON, STRONG_RISK_ON:THEME.regime.RISK_ON, RISK_OFF:THEME.regime.RISK_OFF, STRONG_RISK_OFF:THEME.regime.RISK_OFF, NEUTRAL:THEME.regime.NEUTRAL, CAUTION:THEME.regime.CAUTION };
  const universalLabels = { RISK_ON:"Positive Market Environment", STRONG_RISK_ON:"Strong Positive Market Environment", RISK_OFF:"Defensive Market Environment", STRONG_RISK_OFF:"Strong Defensive Market Environment", NEUTRAL:"Balanced Market Environment", CAUTION:"CAUTION" };
  const label = ak && REGIME_TRANSLATIONS[r]?.[ak] ? REGIME_TRANSLATIONS[r][ak] : (universalLabels[r] || r || "—");
  return { label, color: colors[r] || THEME.regime.UNKNOWN };
}
export function mapIntensity(i, audience) {
  const ak = _audienceKey(audience);
  const descs = { OBSERVE:"Observe — no deployment action", CONSIDER:"Consider positioning — measured evaluation", ACT:"Act on positioning — conditions warrant deployment", MAINTAIN:"Maintain current positioning", PRIORITISE_REDUCTION:"Prioritise portfolio reduction — risk conditions active" };
  const universalLabels = { OBSERVE:"OBSERVE", CONSIDER:"CONSIDER", ACT:"ACT", MAINTAIN:"MAINTAIN", PRIORITISE_REDUCTION:"PRIORITISE REDUCTION" };
  const label = ak && INTENSITY_TRANSLATIONS[i]?.[ak] ? INTENSITY_TRANSLATIONS[i][ak] : (universalLabels[i] || i || "—");
  return { label, desc: descs[i] || "" };
}
export function mapMismatchClass(c, audience) {
  const ak = _audienceKey(audience);
  const descs = { ALIGNED:"Portfolio aligned to regime", MILD_MISMATCH:"Minor portfolio misalignment", MODERATE_MISMATCH:"Moderate portfolio misalignment", SEVERE_MISMATCH:"Significant portfolio misalignment" };
  const universalLabels = { MINIMAL:"Minimal Mismatch", MODERATE:"Moderate Mismatch", SIGNIFICANT:"Significant Mismatch", SUBSTANTIAL:"Substantial Mismatch" };
  const label = ak && MISMATCH_TRANSLATIONS[c]?.[ak] ? MISMATCH_TRANSLATIONS[c][ak] : (universalLabels[c] || c || "—");
  return { label, desc: descs[c] || "" };
}
export function mapDegradationField(field, value) {
  if (!value || value === "OK" || value === "CLEAN") return null;
  const fL = { marketIntelligence:"Market intelligence", portfolioContext:"Portfolio context", advisoryCognition:"Advisory cognition", convictionEngine:"Conviction engine", durabilityEngine:"Regime durability" };
  const vL = { FALLBACK:"confidence based on limited cycle history", INVALID_INPUT:"input validation failed", ERROR:"computation error — deterministic fallback active", SKIPPED:"not applicable", GOVERNANCE_CAPPED:"governance caps applied", MARKET_STATE_UNAVAILABLE:"market state unavailable", NOT_PROVIDED:"not provided", PARTIAL_DEGRADED:"operating with reduced signal confirmation" };
  return { label:`${fL[field]||field}: ${vL[value]||value}`, field, value, governed:true };
}

export function mapMarketTone(tone) {
  return ({
    CAUTIOUS:     { label:"CAUTIOUS",     desc:"Market conditions warrant caution" },
    MEASURED:     { label:"MEASURED",     desc:"Measured analytical tone" },
    BALANCED:     { label:"BALANCED",     desc:"Balanced conditions" },
    CONSTRUCTIVE: { label:"CONSTRUCTIVE", desc:"Constructive market environment" },
    CONFIDENT:    { label:"CONFIDENT",    desc:"High-confidence regime conditions" },
  })[tone] || { label:tone||"—", desc:"" };
}
export function mapDegradationStatus(ds) {
  if (!ds) return [];
  return Object.entries(ds).map(([f,v]) => mapDegradationField(f,v)).filter(Boolean);
}

// ═══════════════════════════════════════════════════════════════════════
// S0-D3 — GOVERNANCE LAYER
// ═══════════════════════════════════════════════════════════════════════
export function evaluateGovernance(narrativeMeta, degradationStatus) {
  const disclosures = [];
  const blockingConditions = [];
  if (!narrativeMeta) return { authorized:true, disclosures:[], blockingConditions:[] };
  const um = narrativeMeta.uncertaintyMode;
  if (um && um.active === true) {
    disclosures.push({ id:"UNCERTAINTY_MODE", type:"uncertainty", priority:1,
      message:`Analytical confidence is qualified — ${um.severity||"ACTIVE"} severity. Treat advisory outputs as observational.`,
      detail: null });
    blockingConditions.push("UNCERTAINTY_MODE_ACTIVE");
  }
  if (narrativeMeta.reversalWindowActive === true) {
    disclosures.push({ id:"REVERSAL_WINDOW", type:"governance", priority:0,
      message:"Mature regime reversal window active. Advisory posture: OBSERVE.",
      detail:"Deployment pacing suppressed during reversal window." });
    blockingConditions.push("REVERSAL_WINDOW_ACTIVE");
  }
  if (narrativeMeta.warmupActive === true) {
    disclosures.push({ id:"WARMUP_ACTIVE", type:"governance", priority:0,
      message:"System warmup active. Advisory brief generation unavailable.", detail:null });
    blockingConditions.push("WARMUP_ACTIVE");
  }
  mapDegradationStatus(degradationStatus).forEach(d => {
    disclosures.push({ id:`DEGRADATION_${d.field.toUpperCase()}`, type:"degradation", priority:2, message:d.label, detail:null });
  });
  const fgs = narrativeMeta.fgsResult;
  if (fgs && fgs.sanitizationsApplied > 0)
    disclosures.push({ id:"FGS_SANITIZED", type:"governance", priority:2, message:"Output governance-filtered.", detail:null });
  if (fgs && fgs.failureFallback === true)
    disclosures.push({ id:"FGS_FALLBACK", type:"governance", priority:1, message:"Advisory output generated via deterministic governance path.", detail:null });
  disclosures.sort((a,b) => a.priority - b.priority);
  return { authorized:true, disclosures, blockingConditions };
}

function GovernanceDisclosure({ disclosure }) {
  return (
    <div style={{ background:THEME.governanceBg, border:`1px solid ${THEME.governanceBorder}`, borderRadius:3, padding:"8px 12px", marginBottom:6 }}>
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", fontWeight:700, color:THEME.governance, letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:2 }}>
        ◐ {({'UNCERTAINTY_MODE':'ANALYTICAL CONFIDENCE NOTICE','DEGRADATION_DURABILITYENGINE':'REGIME DURABILITY NOTICE','DEGRADATION_MARKETINTELLIGENCE':'MARKET INTELLIGENCE NOTICE','REVERSAL_WINDOW':'REGIME REVERSAL ALERT','WARMUP_ACTIVE':'SYSTEM WARMUP','FGS_SANITIZED':'OUTPUT GOVERNANCE','FGS_FALLBACK':'GOVERNANCE PATH ACTIVE'})[disclosure.id] || disclosure.id.replace(/_/g,' ')}
      </div>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.68rem", color:THEME.textSecondary, lineHeight:1.5 }}>{disclosure.message}</div>
      {disclosure.detail && <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.textTertiary, marginTop:3 }}>{disclosure.detail}</div>}
    </div>
  );
}

export function GovernanceLayer({ narrativeMeta, degradationStatus, session, children }) {
  const gov = evaluateGovernance(narrativeMeta, degradationStatus);
  useEffect(() => {
    gov.disclosures.forEach(d => _obsLog({ eventType:"GOVERNANCE_DISCLOSURE_RENDERED", disclosureId:d.id, sessionId:session?.sessionId, timestamp:Date.now() }));
  }, []); // eslint-disable-line
  return (
    <div>
      {gov.disclosures.length > 0 && (
        <div style={{ marginBottom:12 }}>
          {gov.disclosures.map(d => <GovernanceDisclosure key={d.id} disclosure={d} />)}
        </div>
      )}
      {children}
    </div>
  );
}

// Session isolation disclosure — non-dismissible, permanent during portfolio context
// INV-SI-2: renders without gap from context entry to session clear
function SessionIsolationDisclosure({ session }) {
  const active = session.portfolioContext !== null && [
    SESSION_STATES.CONTEXT_ENTRY, SESSION_STATES.PROCESSING,
    SESSION_STATES.POSTURE_ACTIVE, SESSION_STATES.TRACE_ACTIVE,
    SESSION_STATES.SESSION_CLEARING
  ].includes(session.state);
  if (!active) return null;
  return (
    <div style={{ background:THEME.isolationBg, border:`1px solid ${THEME.isolationBorder}`, borderRadius:3, padding:"7px 14px", marginBottom:0, fontFamily:THEME.fontMono, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
      <span style={{ color:THEME.isolation, fontSize:"0.58rem", fontWeight:700, letterSpacing:"0.08em" }}>SESSION CONTEXT ACTIVE</span>
      <span style={{ color:THEME.textTertiary, fontSize:"0.58rem" }}>Portfolio values held in session memory only. Not stored. Cleared on session end.</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// API LAYER
// ═══════════════════════════════════════════════════════════════════════
async function fetchOverview() {
  const r = await fetch("/api/v1/overview");
  if (!r.ok) throw new Error("HTTP " + r.status);
  return r.json();
}
async function fetchHealth() {
  const r = await fetch("/api/v1/health");
  if (!r.ok) throw new Error("HTTP " + r.status);
  return r.json();
}
// POST /api/v1/advisory/brief
// Constitutional: portfolioContext values sent to backend only. Never stored.
async function fetchAdvisoryBrief({ equityPct, debtPct, goldPct, riskProfile, audience, sessionId }) {
  _obsLog({ eventType:"BRIEF_GENERATION_INITIATED", sessionId, audience, riskProfile, timestamp:Date.now() });
  const r = await fetch("/api/v1/advisory/brief", {
    method:"POST",
    headers:{ "Content-Type":"application/json" },
    // Constitutional: sending portfolio values to backend for computation only
    // Values never written to any browser storage
    body: JSON.stringify({ equityPct, debtPct, goldPct, riskProfile, audience, sessionId }),
  });
  const data = await r.json();
  if (!r.ok) return { error: data.error || "API_ERROR", message: data.message || "Advisory intelligence temporarily unavailable.", status: r.status };
  _obsLog({ eventType:"BRIEF_GENERATION_COMPLETED", sessionId, audience, riskProfile,
    scenario: data.data?.advisoryPosture?.scenarioType || null,
    intensity: data.data?.advisoryPosture?.cappedIntensity || null,
    degradation: data.data?.degradationStatus ? Object.values(data.data.degradationStatus).some(v=>v!=="OK") : false,
    latencyMs: Date.now(), timestamp:Date.now() });
  return { data: data.data, dataStatus: data.dataStatus, error: null };
}

function useAmbientIntelligence(intervalMs = 60000) {
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [overviewStatus, setOverviewStatus] = useState("loading");
  const [healthStatus, setHealthStatus] = useState("loading");
  const [lastFetch, setLastFetch] = useState(null);
  const overviewCache = useRef(null);
  const healthCache = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const d = await fetchOverview();
      overviewCache.current = d;
      setOverview(d);
      setOverviewStatus(d.dataStatus || "live");
    } catch(e) {
      if (overviewCache.current) { setOverview(overviewCache.current); setOverviewStatus("stale"); }
      else setOverviewStatus("error");
    }
    try {
      const d = await fetchHealth();
      healthCache.current = d;
      setHealth(d);
      setHealthStatus("live");
    } catch(e) {
      if (healthCache.current) { setHealth(healthCache.current); setHealthStatus("stale"); }
      else setHealthStatus("error");
    }
    setLastFetch(Date.now());
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, intervalMs);
    return () => clearInterval(id);
  }, [refresh, intervalMs]);

  return { overview, health, overviewStatus, healthStatus, lastFetch, refresh };
}

// ═══════════════════════════════════════════════════════════════════════
// SHARED PRIMITIVES
// ═══════════════════════════════════════════════════════════════════════
function Label({ children, style }) {
  return <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:THEME.textMuted, marginBottom:6, ...style }}>{children}</div>;
}
function NamedState({ value, color, size="md" }) {
  const sz = size==="lg"?"1.10rem":size==="xl"?"1.30rem":size==="sm"?"0.68rem":"0.82rem";
  return <div style={{ fontFamily:THEME.fontMono, fontSize:sz, fontWeight:700, color:color||THEME.textPrimary, letterSpacing:"0.06em" }}>{value||"—"}</div>;
}
function SupportingNote({ children }) {
  return <div style={{ fontFamily:THEME.fontSans, fontSize:"0.64rem", color:THEME.textTertiary, marginTop:4, lineHeight:1.5 }}>{children}</div>;
}
function Card({ children, style, accent }) {
  return (
    <div style={{ background:THEME.surfPrimary, border:`1px solid ${accent||THEME.border}`, borderRadius:4, padding:"14px 14px 12px", ...style }}>
      {children}
    </div>
  );
}
function Divider() {
  return <div style={{ height:1, background:THEME.borderSub, margin:"12px 0" }} />;
}
function StatusDot({ status }) {
  const c = status==="live"||status==="OK" ? "#4a8a6a" : status==="stale"||status==="STALE" ? THEME.governance : THEME.textMuted;
  return <span style={{ display:"inline-block", width:6, height:6, borderRadius:"50%", background:c, marginRight:5, verticalAlign:"middle" }} />;
}
function Btn({ children, onClick, variant="primary", disabled }) {
  const bg = variant==="primary" ? THEME.affordance : "transparent";
  const border = variant==="primary" ? THEME.affordance : THEME.border;
  const color = variant==="primary" ? THEME.textPrimary : THEME.textSecondary;
  return (
    <button onClick={onClick} disabled={disabled} style={{ fontFamily:THEME.fontMono, fontSize:"0.62rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", background:disabled?"transparent":bg, border:`1px solid ${disabled?THEME.textMuted:border}`, borderRadius:3, color:disabled?THEME.textMuted:color, padding:"8px 16px", cursor:disabled?"not-allowed":"pointer" }}>
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SURFACE 1 — REGIME INTELLIGENCE TERMINAL (complete from Sprint F1)
// ═══════════════════════════════════════════════════════════════════════
function RegimePrimary({ intelligence }) {
  if (!intelligence) return (
    <Card><Label>Market Environment</Label><NamedState value="—" /><SupportingNote>Awaiting backend intelligence</SupportingNote></Card>
  );
  const regime = mapRegime(intelligence.regime);
  return (
    <Card accent={regime.color+"55"} style={{ borderLeft:`3px solid ${regime.color}` }}>
      <Label>Regime Classification</Label>
      <NamedState value={regime.label} color={regime.color} size="lg" />
      {intelligence.regimeLabel && <SupportingNote>{intelligence.regimeLabel}</SupportingNote>}
      {intelligence.cyclesSinceChange != null && (
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.textTertiary, marginTop:6 }}>
          {intelligence.cyclesSinceChange} {intelligence.cyclesSinceChange===1?"cycle":"cycles"} since last change
        </div>
      )}
    </Card>
  );
}
function DurabilityBlock({ intelligence }) {
  if (!intelligence?.durabilityClass) return <Card><Label>Regime Durability</Label><NamedState value="—" /></Card>;
  const dur = mapDurabilityClass(intelligence.durabilityClass);
  return <Card><Label>Regime Durability</Label><NamedState value={dur.label} /><SupportingNote>{dur.desc}</SupportingNote></Card>;
}
function ConvictionBlock({ intelligence }) {
  if (!intelligence?.convictionClass) return <Card><Label>Advisory Conviction</Label><NamedState value="—" /></Card>;
  const conv = mapConvictionClass(intelligence.convictionClass);
  const traj = intelligence.convictionTrajectory;
  const trajLabel = traj==="RISING"?"↑ Rising":traj==="FALLING"?"↓ Falling":traj==="STABLE"?"→ Stable":null;
  return (
    <Card>
      <Label>Advisory Conviction</Label>
      <NamedState value={conv.label} />
      {trajLabel && <div style={{ fontFamily:THEME.fontMono, fontSize:"0.60rem", color:traj==="RISING"?THEME.affordance:traj==="FALLING"?THEME.governance:THEME.textTertiary, marginTop:6 }}>{trajLabel}</div>}
      <SupportingNote>{conv.desc}</SupportingNote>
    </Card>
  );
}
function ActionBiasBlock({ intelligence }) {
  const bias = intelligence?.actionBias || null;
  if (!bias) return null;
  return <Card><Label>Suggested Action</Label><NamedState value={bias} /></Card>;
}
function SignalStrip({ overview }) {
  const strip = overview?.data?.signalStrip || [];
  if (strip.length === 0) return null;
  return (
    <Card>
      <Label>Signal Alignment</Label>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:4 }}>
        {strip.map((s,i) => {
          const sig = s.signal||s.sentiment||"—";
          const c = (sig==="POSITIVE"||sig==="BUY") ? THEME.affordance : (sig==="NEGATIVE"||sig==="SELL") ? THEME.governance : THEME.textTertiary;
          return <div key={i} style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", padding:"3px 8px", border:`1px solid ${c}50`, borderRadius:2, color:c, background:`${c}10` }}>{s.name||s.source||"—"} — {sig}</div>;
        })}
      </div>
    </Card>
  );
}
function ModulesDetail({ overview, open, onToggle }) {
  const modules = overview?.data?.modules || {};
  const entries = Object.entries(modules);
  if (entries.length === 0) return null;
  return (
    <div>
      <button onClick={onToggle} style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.affordance, background:"transparent", border:`1px solid ${THEME.affordance}40`, borderRadius:2, padding:"4px 10px", cursor:"pointer", letterSpacing:"0.08em", textTransform:"uppercase" }}>
        {open?"▲ Hide module detail":"▼ Module signal detail"}
      </button>
      {open && (
        <Card style={{ marginTop:8 }}>
          <Label>Module Breakdown</Label>
          {entries.map(([key,mod],i) => {
            const included = mod.included !== false;
            const sl = mod.dataStatus||mod.status||null;
            return (
              <div key={key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:i<entries.length-1?`1px solid ${THEME.borderSub}`:"none" }}>
                <span style={{ fontFamily:THEME.fontMono, fontSize:"0.62rem", fontWeight:700, color:included?THEME.textPrimary:THEME.textMuted, textTransform:"uppercase", letterSpacing:"0.06em" }}>{key}{!included&&<span style={{ fontFamily:THEME.fontMono, fontSize:"0.50rem", color:THEME.textMuted, marginLeft:6 }}>excl</span>}</span>
                {sl && <span style={{ fontFamily:THEME.fontMono, fontSize:'0.54rem', color:sl==='live'?THEME.affordance:sl==='fallback'?THEME.governance:THEME.textMuted }}>{sl==='fallback'?'LIMITED':sl==='live'?'LIVE':sl.toUpperCase()}</span>}
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
function SystemHealthBlock({ health, healthStatus }) {
  if (!health) return <Card><Label>System Health</Label><NamedState value={healthStatus==="loading"?"Connecting…":"Unavailable"} color={THEME.textMuted} size="sm" /></Card>;
  const overall = health.status||"UNKNOWN";
  const confClass = health.confidence?.classification||null;
  // T3: Institutional label mapping — backend value preserved, display label calibrated
  const HEALTH_DISPLAY_LABELS = {
    'OK': 'OPERATIONAL',
    'HEALTHY_WITH_WARNINGS': 'OPERATIONALLY STABLE',
    'PARTIAL_DEGRADED': 'PRIMARY SOURCE IMPAIRED',
    'DEGRADED': 'ADVISORY CAPABILITY REDUCED',
    'DOWN': 'SYSTEM UNAVAILABLE',
  };
  const overallLabel = HEALTH_DISPLAY_LABELS[overall] || overall;
  const overallColor = (overall==='OK'||overall==='HEALTHY_WITH_WARNINGS')?THEME.affordance:overall==='PARTIAL_DEGRADED'?THEME.governance:(overall==='DEGRADED'||overall==='DOWN')?THEME.governance:THEME.textMuted;
  return (
    <Card>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
        <Label style={{ marginBottom:0 }}>System Health</Label>
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.60rem", fontWeight:700, color:overallColor }}>{overallLabel}</div>
      </div>
      {(() => {
        const srcs = health.sources || {};
        const PRIMARY = ['NSE','RBI','MOSPI','FRED','BLS','FedRSS'];
        const primaryOk = PRIMARY.filter(n => srcs[n]?.status === 'OK').length;
        const totalPrimary = PRIMARY.length;
        return (
          <div style={{ marginBottom:10, display:'flex', gap:16, flexWrap:'wrap' }}>
            <div><span style={{ fontFamily:THEME.fontMono, fontSize:'0.52rem', color:THEME.textTertiary }}>OPERATIONAL STATUS: </span><span style={{ fontFamily:THEME.fontMono, fontSize:'0.52rem', fontWeight:700, color:THEME.affordance }}>STABLE</span></div>
            <div><span style={{ fontFamily:THEME.fontMono, fontSize:'0.52rem', color:THEME.textTertiary }}>SIGNAL COVERAGE: </span><span style={{ fontFamily:THEME.fontMono, fontSize:'0.52rem', fontWeight:700, color:primaryOk>=5?THEME.affordance:THEME.governance }}>{primaryOk}/{totalPrimary}</span></div>
          </div>
        );
      })()}
      <Divider />
      {/* T1/T2: Primary sources — institutional prominence */}
      {(() => {
        const PRIMARY = ['NSE','RBI','MOSPI','FRED','BLS','FedRSS'];
        const smartapiStatus = (() => { const sa=health.smartapi; if(!sa) return null; if(sa.loginStatus==='SUCCESS'&&sa.jwtActive) return 'OPERATIONAL'; if(sa.loginStatus==='SUCCESS'&&!sa.jwtActive) return 'ACTIVE'; if(sa.loginStatus==='FAILED') return 'AUTH REQUIRED'; if(sa.loginStatus==='NOT_ATTEMPTED') return 'PENDING'; return null; })();
        const SECONDARY = ['Yahoo'];
        const allSources = health.sources || {};
        const primaryEntries = Object.entries(allSources).filter(([n]) => PRIMARY.includes(n));
        const secondaryEntries = Object.entries(allSources).filter(([n]) => SECONDARY.includes(n));
        // T4: Institutional status labels — no engineering "fallback" terminology
        const statusLabel = (status) => ({
          'OK': 'OK', 'STALE': 'LIMITED', 'DOWN': 'UNAVAILABLE', 'UNKNOWN': 'PENDING'
        })[status] || status;
        return (
          <div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
              {primaryEntries.map(([name,info]) => {
                const sc = info.status==='OK'?THEME.affordance:info.status==='STALE'?THEME.governance:THEME.textMuted;
                return <div key={name} style={{ fontFamily:THEME.fontMono, fontSize:'0.52rem', padding:'2px 7px', border:`1px solid ${sc}40`, borderRadius:2, color:sc }}><StatusDot status={info.status}/>{name} — {statusLabel(info.status)}</div>;
              })}
              {smartapiStatus && (()=>{ const sc=smartapiStatus==='OPERATIONAL'||smartapiStatus==='ACTIVE'?THEME.affordance:THEME.governance; return <div style={{ fontFamily:THEME.fontMono, fontSize:'0.52rem', padding:'2px 7px', border:`1px solid ${sc}40`, borderRadius:2, color:sc }}><StatusDot status={smartapiStatus==='OPERATIONAL'?'OK':'STALE'}/>SmartAPI — {smartapiStatus}</div>; })()}
            </div>
            {/* T2/T4: Secondary sources — minimal visual weight */}
            {secondaryEntries.length > 0 && (
              <div style={{ marginTop:10, paddingTop:6, borderTop:`1px solid ${THEME.borderSub}` }}>
                <div style={{ fontFamily:THEME.fontMono, fontSize:'0.44rem', color:THEME.textMuted, letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:3, opacity:0.6 }}>Supplementary telemetry</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                  {secondaryEntries.map(([name,info]) => {
                    const sc = info.status==='OK'?THEME.textTertiary:THEME.textMuted;
                    return <div key={name} style={{ fontFamily:THEME.fontMono, fontSize:'0.46rem', padding:'1px 5px', border:`1px solid ${THEME.borderSub}`, borderRadius:2, color:sc, opacity:0.55 }}>{name}: {statusLabel(info.status)}</div>;
                  })}
                </div>
              </div>
            )}
            {/* Primary-only alerts — suppress LOW severity supplementary alerts from client view */}
            {health.alerts?.filter(a => a.severity !== 'LOW').map((a,i) => {
                const AL = {'FETCH_FAILURE_REPEATED':'Supplementary telemetry temporarily unavailable','STALE_THRESHOLD_BREACH':'Secondary market data delayed','CIRCUIT_BREAKER_OPEN':'Supplementary source temporarily suspended','TIMEOUT':'Supplementary source connection delayed','DOWN':'Source unavailable'};
                const lbl = AL[a.code] || 'Secondary telemetry limited';
                return <div key={i} style={{ fontFamily:THEME.fontMono, fontSize:'0.56rem', color:THEME.governance, marginTop:6 }}>◐ {a.source}: {lbl}</div>;
            })}
          </div>
        );
      })()}
    </Card>
  );
}
function LastUpdated({ ts, status }) {
  const [age, setAge] = useState("");
  useEffect(() => {
    const tick = () => { if (!ts){setAge("");return;} const s=Math.floor((Date.now()-ts)/1000); setAge(s<10?"just now":s<60?`${s}s ago`:`${Math.floor(s/60)}m ago`); };
    tick(); const id=setInterval(tick,5000); return ()=>clearInterval(id);
  }, [ts]);
  return <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, display:"flex", alignItems:"center", gap:6 }}><StatusDot status={status}/>{age?`Updated ${age}`:"—"}</div>;
}

function Surface1({ session }) {
  const { overview, health, overviewStatus, healthStatus, lastFetch } = useAmbientIntelligence(60000);
  const [modulesOpen, setModulesOpen] = useState(false);
  const intelligence = overview?.data?.intelligence || null;
  const confidenceDrivers = overview?.data?.confidence?.drivers || null;
  const degradationFromOverview = confidenceDrivers
    ? Object.fromEntries(Object.entries(confidenceDrivers).map(([k,v]) => [k, v.status==="live"?"OK":v.status?.toUpperCase()||"UNKNOWN"]))
    : null;

  return (
    <div style={{ padding:"14px 14px 40px", maxWidth:560, margin:"0 auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:THEME.textMuted }}>Regime Intelligence Terminal</div>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textTertiary, marginTop:2 }}>Surface 1 — Ambient intelligence</div>
        </div>
        <LastUpdated ts={lastFetch} status={overviewStatus} />
      </div>
      <GovernanceLayer narrativeMeta={null} degradationStatus={degradationFromOverview} session={session}>
        <RegimePrimary intelligence={intelligence} />
        <div style={{ height:10 }} />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <DurabilityBlock intelligence={intelligence} />
          <ConvictionBlock intelligence={intelligence} />
        </div>
        <div style={{ height:10 }} />
        <ActionBiasBlock intelligence={intelligence} />
        <div style={{ height:10 }} />
        <SignalStrip overview={overview} />
        <div style={{ height:10 }} />
        <ModulesDetail overview={overview} open={modulesOpen} onToggle={() => setModulesOpen(v=>!v)} />
        <div style={{ height:16 }} />
        <Divider />
        <div style={{ height:10 }} />
        <SystemHealthBlock health={health} healthStatus={healthStatus} />
      </GovernanceLayer>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// NARRATIVE ORCHESTRATION RENDERING LAYER
// Renders LLM-generated narrative fields from /api/v1/advisory/brief
// Constitutional: all fields from FGS-processed backend response only
// No frontend text generation. No local inference.
// Governance: confidenceNote renders only when present (backend-controlled)
// ═══════════════════════════════════════════════════════════════════════

// MarketToneIndicator — named abstraction only, never raw string
function MarketToneIndicator({ tone }) {
  if (!tone) return null;
  const mapped = mapMarketTone(tone);
  const toneColor = tone==="CAUTIOUS"?THEME.governance:tone==="MEASURED"||tone==="BALANCED"?THEME.textSecondary:THEME.affordance;
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:5, fontFamily:THEME.fontMono, fontSize:"0.58rem", fontWeight:700, color:toneColor, letterSpacing:"0.08em", border:`1px solid ${toneColor}40`, borderRadius:2, padding:"2px 8px", background:`${toneColor}10` }}>
      {mapped.label}
    </div>
  );
}

// NarrativeSummaryBlock — primary narrative text
// Rendered from backend FGS output only (G1-FGS)
// Language-forward — this is the primary prose surface
function NarrativeSummaryBlock({ briefData }) {
  const summary = briefData?.summary || null;
  const tone = briefData?.marketTone || null;
  if (!summary) return null;
  return (
    <Card>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
        <Label style={{ marginBottom:0 }}>Market Intelligence Summary</Label>
        <MarketToneIndicator tone={tone} />
      </div>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.76rem", color:THEME.textPrimary, lineHeight:1.7 }}>
        {summary}
      </div>
    </Card>
  );
}

// KeyDriversBlock — numbered driver list from backend
// Each driver is a complete sentence from LLM/rulebased — never parsed or reinterpreted
function KeyDriversBlock({ briefData }) {
  const drivers = briefData?.keyDrivers || [];
  if (drivers.length === 0) return null;
  return (
    <Card>
      <Label>Key Market Drivers</Label>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {drivers.map((d, i) => (
          <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontFamily:THEME.fontMono, fontSize:"0.54rem", fontWeight:700, color:THEME.textMuted, flexShrink:0, paddingTop:3, minWidth:14 }}>{i+1}.</span>
            <span style={{ fontFamily:THEME.fontSans, fontSize:"0.70rem", color:THEME.textSecondary, lineHeight:1.6 }}>{d}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// TacticalViewBlock — allocation posture from narrative engine
// Distinct from ACL postureStatement — audience-calibrated tactical framing
function TacticalViewBlock({ briefData }) {
  const tactical = briefData?.tacticalView || null;
  if (!tactical) return null;
  return (
    <Card>
      <Label>Tactical View</Label>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.72rem", color:THEME.textPrimary, lineHeight:1.65 }}>
        {tactical}
      </div>
    </Card>
  );
}

// MacroContextBlock — macro framing when present
function MacroContextBlock({ briefData }) {
  const macro = briefData?.macroContext || null;
  if (!macro) return null;
  return (
    <Card>
      <Label>Macro Context</Label>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.68rem", color:THEME.textSecondary, lineHeight:1.65 }}>
        {macro}
      </div>
    </Card>
  );
}

// ConfidenceNoteBlock — renders ONLY when backend provides it (confidence < 0.60)
// INV-GR-4: adjacent to intelligence it qualifies
// Never rendered as a positive statement — only as qualification
function ConfidenceNoteBlock({ briefData }) {
  const note = briefData?.confidenceNote || null;
  if (!note) return null;
  return (
    <div style={{ background:THEME.governanceBg, border:`1px solid ${THEME.governanceBorder}`, borderRadius:3, padding:"8px 12px" }}>
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", fontWeight:700, color:THEME.governance, letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:3 }}>
        ◐ CONVICTION QUALIFICATION
      </div>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.66rem", color:THEME.textSecondary, lineHeight:1.5 }}>
        {note}
      </div>
    </div>
  );
}

// RiskFlagsBlock — risk flags from backend, named only
function RiskFlagsBlock({ briefData }) {
  const flags = briefData?.riskFlags || [];
  if (flags.length === 0) return null;
  return (
    <Card>
      <Label>Risk Flags</Label>
      <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
        {flags.map((f, i) => (
          <div key={i} style={{ fontFamily:THEME.fontSans, fontSize:"0.66rem", color:THEME.textSecondary, lineHeight:1.5, display:"flex", gap:7, alignItems:"flex-start" }}>
            <span style={{ color:THEME.governance, flexShrink:0, paddingTop:2 }}>◐</span>
            <span>{f}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// DisclaimerBlock — renders when backend provides disclaimer (degraded confidence)
function DisclaimerBlock({ briefData }) {
  const disclaimer = briefData?.disclaimer || null;
  if (!disclaimer) return null;
  return (
    <div style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textMuted, lineHeight:1.5, padding:"6px 0", borderTop:`1px solid ${THEME.borderSub}` }}>
      {disclaimer}
    </div>
  );
}

// NarrativeBriefBlock — complete narrative orchestration block
// Renders all LLM/narrative fields in governed order
// Order: summary → keyDrivers → tacticalView → macroContext → confidenceNote → riskFlags → disclaimer
// This order is constitutionally determined — not aesthetic
function NarrativeBriefBlock({ briefData }) {
  const [expanded, setExpanded] = useState(false);
  if (!briefData?.summary && !briefData?.keyDrivers?.length) return null;
  return (
    <div>
      <NarrativeSummaryBlock briefData={briefData} />
      <div style={{ height:10 }} />
      <KeyDriversBlock briefData={briefData} />
      <div style={{ height:10 }} />
      {/* Tactical view + macro — one deliberate expand (FSD S3.1 cognitive separation) */}
      <button onClick={() => setExpanded(v=>!v)} style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.affordance, background:"transparent", border:`1px solid ${THEME.affordance}40`, borderRadius:2, padding:"4px 10px", cursor:"pointer", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom: expanded ? 10 : 0 }}>
        {expanded ? "▲ Hide detail" : "▼ Tactical detail + macro"}
      </button>
      {expanded && (
        <div>
          <TacticalViewBlock briefData={briefData} />
          <div style={{ height:10 }} />
          <MacroContextBlock briefData={briefData} />
          <div style={{ height:10 }} />
          <RiskFlagsBlock briefData={briefData} />
        </div>
      )}
      <div style={{ height:10 }} />
      <ConfidenceNoteBlock briefData={briefData} />
      <DisclaimerBlock briefData={briefData} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SURFACE 2 — ADVISORY BRIEF WORKSPACE
// Session-scoped. Procedural workflow.
// Consumes: POST /api/v1/advisory/brief
// Constitutional: portfolio values memory-only, never stored
// ═══════════════════════════════════════════════════════════════════════

const AUDIENCES = ["IFA Advisory Pitch","Wealth Manager Synthesis","Family Office Risk Brief","Private Banker Advisory","Advanced Investor Dashboard","Retail Investor","HNI Investor","Conservative Retiree","Aggressive Growth Investor","Institutional Strategy Desk"];
const RISK_PROFILES = ["Conservative","Moderate","Aggressive"];

// Portfolio input form — CONTEXT_ENTRY state
function PortfolioInputForm({ onSubmit, initialValues }) {
  const [equity, setEquity] = useState(initialValues?.equityPct != null ? String(initialValues.equityPct) : "");
  const [debt, setDebt] = useState(initialValues?.debtPct != null ? String(initialValues.debtPct) : "");
  const [gold, setGold] = useState(initialValues?.goldPct != null ? String(initialValues.goldPct) : "");
  const [risk, setRisk] = useState(initialValues?.riskProfile || "Moderate");
  const [audience, setAudience] = useState(initialValues?.audience || "IFA Advisory Pitch");
  const [validationError, setValidationError] = useState(null);

  const eq = parseFloat(equity)||0;
  const db = parseFloat(debt)||0;
  const gd = parseFloat(gold)||0;
  const total = eq + db + gd;
  const sumOk = Math.abs(total - 100) < 0.01;

  const handleGenerate = () => {
    setValidationError(null);
    // Negative value guard — governance-safe: negative allocations are not valid
    if (eq < 0 || db < 0 || gd < 0) {
      setValidationError('Portfolio allocations cannot be negative. Please enter values between 0 and 100.');
      return;
    }
    // Individual component bounds
    if (eq > 100 || db > 100 || gd > 100) {
      setValidationError('No individual allocation may exceed 100%.');
      return;
    }
    // Sum check — must equal 100%
    if (!sumOk) {
      setValidationError(`Portfolio allocations sum to ${total.toFixed(1)}%. Must equal 100% before generating advisory brief.`);
      return;
    }
    onSubmit({ equityPct:eq, debtPct:db, goldPct:gd>0?gd:null, riskProfile:risk, audience });
  };

  const inputStyle = { fontFamily:THEME.fontMono, fontSize:"0.72rem", color:THEME.textPrimary, background:THEME.surfSec, border:`1px solid ${THEME.border}`, borderRadius:3, padding:"8px 10px", width:"100%", outline:"none" };
  const selectStyle = { ...inputStyle, cursor:"pointer" };

  return (
    <Card>
      <Label>Portfolio Context</Label>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:12 }}>
        {[["Equity %", equity, setEquity],["Debt %", debt, setDebt],["Gold %", gold, setGold]].map(([lbl,val,set],i) => {
          const numVal = parseFloat(val)||0;
          const isNeg = numVal < 0;
          const fieldStyle = { ...inputStyle, borderColor: isNeg ? THEME.governance : THEME.border };
          return (
            <div key={i}>
              <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:isNeg?THEME.governance:THEME.textMuted, marginBottom:4, letterSpacing:"0.10em" }}>{lbl}{isNeg&&" ✗"}</div>
              <input type="number" min="0" max="100" value={val}
                onChange={e => { set(e.target.value); }}
                style={fieldStyle} placeholder="0" />
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:total===0?THEME.textMuted:sumOk?THEME.affordance:THEME.governance, letterSpacing:"0.08em" }}>
          {total===0?"Allocations: —":`Total: ${total.toFixed(1)}%${sumOk?" ✓":" — must equal 100%"}`}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
        <div>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginBottom:4, letterSpacing:"0.10em" }}>Risk Profile</div>
          <select value={risk} onChange={e=>setRisk(e.target.value)} style={selectStyle}>
            {RISK_PROFILES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginBottom:4, letterSpacing:"0.10em" }}>Audience</div>
          <select value={audience} onChange={e=>setAudience(e.target.value)} style={selectStyle}>
            {AUDIENCES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>
      {validationError && (
        <div style={{ fontFamily:THEME.fontSans, fontSize:"0.66rem", color:THEME.governance, background:THEME.governanceBg, border:`1px solid ${THEME.governanceBorder}`, borderRadius:3, padding:"7px 10px", marginBottom:12, lineHeight:1.5 }}>
          {validationError}
        </div>
      )}
      <Btn onClick={handleGenerate} disabled={total===0||eq<0||db<0||gd<0}>Generate Advisory Brief</Btn>
    </Card>
  );
}

// Processing state — named, no partial results, no AI theater
function ProcessingState() {
  return (
    <Card>
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.62rem", color:THEME.textTertiary, letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:8 }}>
        Generating advisory brief
      </div>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.68rem", color:THEME.textSecondary, lineHeight:1.6 }}>
        Computing governed advisory posture. All inputs locked.
      </div>
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textMuted, marginTop:8 }}>
        Backend processing — no partial results displayed.
      </div>
    </Card>
  );
}

// Advisory posture block — primary information layer
// Conclusions-first: posture statement first, conviction second, evidence on demand
function AdvisoryPostureBlock({ briefData }) {
  const acl = briefData?.advisoryPosture;
  const conviction = briefData?.conviction;
  const narrativeMeta = briefData?.narrativeMeta;

  if (!acl) return (
    <Card>
      <Label>Advisory Posture</Label>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.68rem", color:THEME.textTertiary }}>
        Advisory posture unavailable — portfolio context required.
      </div>
    </Card>
  );

  const _s2audience = briefData?.narrativeMeta?.audience || null;
  const intensity = mapIntensity(acl.cappedIntensity, _s2audience);
  const intensityColor = acl.cappedIntensity==="OBSERVE"?THEME.governance:acl.cappedIntensity==="ACT"?THEME.affordance:THEME.textSecondary;

  return (
    <div>
      {/* PRIMARY: Posture statement — single opacity fade on first render (FSD 5.4) */}
      <Card accent={intensityColor+"55"} style={{ borderLeft:`3px solid ${intensityColor}`, animation:"iael-posture-appear 180ms ease-in" }}>
        <style>{`@keyframes iael-posture-appear{from{opacity:0}to{opacity:1}}`}</style>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
          <Label style={{ marginBottom:0 }}>Advisory Posture</Label>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.62rem", fontWeight:700, color:intensityColor, letterSpacing:"0.10em" }}>
            {intensity.label}
          </div>
        </div>
        {/* Posture statement — FGS-processed backend output only. Never frontend-generated. */}
        {acl.postureStatement && (
          <div style={{ fontFamily:THEME.fontSans, fontSize:"0.78rem", color:THEME.textPrimary, lineHeight:1.65, marginBottom:10 }}>
            {acl.postureStatement}
          </div>
        )}
        {/* Conviction qualifier */}
        {conviction?.convictionClass && (
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.textSecondary, marginBottom:6 }}>
            Conviction: {mapConvictionClass(conviction.convictionClass).label}
          </div>
        )}
        {/* Deployment pacing — when present */}
        {acl.deploymentPacing && (
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.textTertiary }}>
            Deployment: {acl.deploymentPacing.tranches} tranches over {acl.deploymentPacing.periodWeeks} weeks
          </div>
        )}
      </Card>

      <div style={{ height:10 }} />

      {/* NARRATIVE: LLM-generated summary, drivers, tactical view */}
      {/* All fields from FGS-processed backend response — no frontend text generation */}
      <NarrativeBriefBlock briefData={briefData} />

      <div style={{ height:10 }} />

      {/* SUPPORTING: Portfolio context summary */}
      {briefData.portfolioContext && <PortfolioContextSummary pce={briefData.portfolioContext} audience={briefData?.narrativeMeta?.audience} />}
      {briefData?.pciAssessment && <PCICard pci={briefData.pciAssessment} />}

      <div style={{ height:10 }} />

      {/* Stress awareness note — when present */}
      {acl.stressAwarenessNote && (
        <Card>
          <Label>Historical Portfolio Reference</Label>
          <div style={{ fontFamily:THEME.fontSans, fontSize:"0.68rem", color:THEME.textSecondary, lineHeight:1.6 }}>{acl.stressAwarenessNote}</div>
        </Card>
      )}

      {/* Market context — regime framing this session */}
      {briefData.marketContext && (
        <div style={{ marginTop:10 }}>
          <Card>
            <Label>Session Market Context</Label>
            <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
              {briefData.marketContext.regime && (
                <div>
                  <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginBottom:3 }}>REGIME</div>
                  <NamedState value={mapRegime(briefData.marketContext.regime, briefData?.narrativeMeta?.audience).label} color={mapRegime(briefData.marketContext.regime, briefData?.narrativeMeta?.audience).color} size="sm" />
                </div>
              )}
              {briefData.marketContext.durabilityClass && (
                <div>
                  <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginBottom:3 }}>DURABILITY</div>
                  <NamedState value={mapDurabilityClass(briefData.marketContext.durabilityClass, briefData?.narrativeMeta?.audience).label} size="sm" />
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// Portfolio context summary — supporting layer
// Renders mismatchClass (named), mismatchDirection (named). Never numeric magnitude.
function PortfolioContextSummary({ pce, audience }) {
  if (!pce) return null;
  const _pceAudience = audience || null;
  const mismatch = pce.mismatchClass ? mapMismatchClass(pce.mismatchClass, _pceAudience) : null;
  const direction = pce.mismatchDirection || null;
  const mappedDirection = direction && direction !== 'NONE' ? mapDirection(direction, _pceAudience) : null;

  return (
    <Card>
      <Label>Portfolio Alignment</Label>
      <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
        {mismatch && (
          <div>
            <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginBottom:3 }}>ALIGNMENT</div>
            <NamedState value={mismatch.label} size="sm" />
          </div>
        )}
        {mappedDirection && (
          <div>
            <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginBottom:3 }}>DIRECTION</div>
            <NamedState value={mappedDirection.label} size="sm" />
          </div>
        )}
        {pce.scenarioType && (
          <div>
            <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginBottom:3 }}>SCENARIO</div>
            <NamedState value={pce.scenarioType} size="sm" />
          </div>
        )}
      </div>
      {mismatch && <SupportingNote>{mismatch.desc}</SupportingNote>}
    </Card>
  );
}

// Advisory endpoint failure state — named, honest
// No cached posture. No inferred posture. (FSD 11.2)
function AdvisoryFailureState({ error, onRetry }) {
  return (
    <Card>
      <Label>Advisory Intelligence</Label>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.70rem", color:THEME.textSecondary, lineHeight:1.6, marginBottom:12 }}>
        Advisory intelligence temporarily unavailable. Market intelligence remains available.
      </div>
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.governance, marginBottom:12 }}>
        {"Advisory intelligence temporarily unavailable. Please retry."}
      </div>
      <Btn onClick={onRetry} variant="secondary">Retry</Btn>
    </Card>
  );
}

// Session clearing confirmation — exactly once, session clearing only (FSD 6.5)
function SessionClearingDialog({ onConfirm, onCancel }) {
  return (
    <Card accent={THEME.border}>
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.72rem", color:THEME.textPrimary, marginBottom:16 }}>
        Clear session context?
      </div>
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.textTertiary, marginBottom:16, lineHeight:1.5 }}>
        All portfolio values and advisory posture will be permanently removed from session memory.
      </div>
      <div style={{ display:"flex", gap:10 }}>
        <Btn onClick={onConfirm}>Clear</Btn>
        <Btn onClick={onCancel} variant="secondary">Continue session</Btn>
      </div>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// PCI FRONTEND — Session 5
// Surface 2: PCICard — named classes + diagnostic conclusion only
// Surface 3: PCIAuditBlock — full decision path trace
// Authority: PCI_IMPLEMENTATION_SPEC_v1.1 §10, §11
// Constitutional: No scores, no band values on Surface 2
// ═══════════════════════════════════════════════════════════════════════

const PCI_CLASS_LABELS = {
  CONCENTRATION_ALERT:          'Concentration Alert',
  ATTENTION_REQUIRED:           'Attention Required',
  REVIEW_WARRANTED:             'Review Warranted',
  WELL_POSITIONED:              'Well Positioned',
  SEVERE_CONCENTRATION:         'Severe Concentration',
  ELEVATED_CONCENTRATION:       'Elevated Concentration',
  MODERATE_CONCENTRATION:       'Moderate Concentration',
  NORMAL_CONCENTRATION:         'Normal',
  UNDIVERSIFIED:                'Undiversified',
  MINIMALLY_DIVERSIFIED:        'Minimally Diversified',
  PARTIALLY_DIVERSIFIED:        'Partially Diversified',
  FULLY_DIVERSIFIED:            'Fully Diversified',
  SIGNIFICANTLY_ABOVE_PROFILE:  'Significantly Above Profile',
  ABOVE_PROFILE:                'Above Declared Profile',
  WITHIN_PROFILE:               'Aligned With Profile',
  BELOW_PROFILE:                'Below Declared Profile',
  SIGNIFICANTLY_BELOW_PROFILE:  'Significantly Below Profile',
  EXTREME_IMBALANCE:            'Extreme Imbalance',
  SIGNIFICANTLY_IMBALANCED:     'Significantly Imbalanced',
  MODERATELY_IMBALANCED:        'Moderately Imbalanced',
  WELL_BALANCED:                'Well Balanced',
  HIGH_RESILIENCE:              'High Resilience',
  MODERATE_RESILIENCE:          'Moderate Resilience',
  LIMITED_RESILIENCE:           'Limited Resilience',
};

const PCI_COMPOSITE_COLORS = {
  CONCENTRATION_ALERT: '#ff4d4d',
  ATTENTION_REQUIRED:  '#ff9f40',
  REVIEW_WARRANTED:    '#f7c948',
  WELL_POSITIONED:     '#4caf82',
};

function pciLabel(cls) {
  return PCI_CLASS_LABELS[cls] || cls || '—';
}

// Surface 2 — PCICard
// Shows: composite class, diagnostic conclusion, 5 assessment labels
// Never shows: scores, band values, numeric weights
function PCICard({ pci }) {
  if (!pci) return null;
  const color = PCI_COMPOSITE_COLORS[pci.compositeClass] || THEME.textSecondary;
  return (
    <Card style={{ marginBottom: 14, borderLeft: '3px solid ' + color }}>
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem', fontWeight: 700,
        letterSpacing: '0.15em', textTransform: 'uppercase', color: THEME.textMuted, marginBottom: 6 }}>
        Portfolio Construction Assessment
      </div>
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.72rem', fontWeight: 700,
        color: color, marginBottom: 8, letterSpacing: '0.04em' }}>
        {pciLabel(pci.compositeClass)}
      </div>
      {pci.diagnosticConclusion && (
        <div style={{ fontFamily: THEME.fontSans, fontSize: '0.72rem', color: THEME.textSecondary,
          lineHeight: 1.6, marginBottom: 10 }}>
          {pci.diagnosticConclusion}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
        {[
          ['Concentration', pci.concentrationClass],
          ['Diversification', pci.diversificationClass],
          ['Risk Alignment', pci.riskAlignmentClass],
          ['Asset Balance', pci.assetBalanceClass],
          ['Resilience', pci.resilienceClass],
        ].map(([dim, cls]) => (
          <div key={dim} style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem',
            color: THEME.textMuted, background: THEME.surface2 || 'rgba(255,255,255,0.04)',
            borderRadius: 4, padding: '3px 7px', letterSpacing: '0.04em' }}>
            <span style={{ color: THEME.textTertiary }}>{dim}: </span>
            <span style={{ color: THEME.textSecondary }}>{pciLabel(cls)}</span>
          </div>
        ))}
      </div>
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.48rem', color: THEME.textMuted,
        marginTop: 8, opacity: 0.6 }}>
        PCI v1.1 · {pci.pciNarrativeSource || 'canonical'} · FGS: {pci.pciFgsStatus || '—'}
      </div>
    </Card>
  );
}

// Surface 3 — PCIAuditBlock
// Shows: full decision path for all 6 assessments + ASE audit
function PCIAuditBlock({ pci }) {
  if (!pci || !pci.pciAudit) return null;
  const audit = pci.pciAudit;
  const inp   = audit.inputs || {};
  const ref   = audit.contractRef || {};
  const scores = audit.assessmentScores || {};
  const comp  = audit.compositeComputation || {};
  const ase   = audit.aseAudit || {};

  return (
    <AuditSection title="PCI Portfolio Construction Audit">

      {/* 1. Inputs */}
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem', color: THEME.textMuted,
        letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>Inputs</div>
      <AuditRow label="Equity" value={inp.equityPct != null ? inp.equityPct + '%' : '—'} />
      <AuditRow label="Debt" value={inp.debtPct != null ? inp.debtPct + '%' : '—'} />
      <AuditRow label="Gold" value={inp.goldPct != null ? inp.goldPct + '%' : '—'} />
      <AuditRow label="Risk Profile" value={inp.riskProfile || '—'} />
      <AuditRow label="Audience Tier" value={inp.tier != null ? 'Tier ' + inp.tier : '—'} />
      <AuditRow label="Regime" value={inp.regime || '—'} />

      {/* 2. Contract Reference */}
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem', color: THEME.textMuted,
        letterSpacing: '0.10em', textTransform: 'uppercase', margin: '8px 0 4px' }}>Contract Reference</div>
      {ref.equity && <AuditRow label="Equity Band" value={ref.equity.lower + '–' + ref.equity.upper + '% (mid ' + ref.equity.midpoint + '%)'} />}
      {ref.debt   && <AuditRow label="Debt Band"   value={ref.debt.lower   + '–' + ref.debt.upper   + '% (mid ' + ref.debt.midpoint   + '%)'} />}
      {ref.gold   && <AuditRow label="Gold Band"   value={ref.gold.lower   + '–' + ref.gold.upper   + '% (mid ' + ref.gold.midpoint   + '%)'} />}

      {/* 3. Decision Paths */}
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem', color: THEME.textMuted,
        letterSpacing: '0.10em', textTransform: 'uppercase', margin: '8px 0 4px' }}>Decision Paths</div>

      <AuditRow label="Concentration" value={pciLabel(pci.concentrationClass) + ' (severity ' + (scores.concentrationSeverity ?? '—') + ')'} />
      {scores.maxAlloc != null && <AuditRow label="  Max Alloc" value={scores.maxAlloc + '%'} />}
      <AuditRow label="Diversification" value={pciLabel(pci.diversificationClass) + ' (severity ' + (scores.diversificationSeverity ?? '—') + ')'} />
      {scores.participatingClasses != null && <AuditRow label="  Participating" value={scores.participatingClasses + ' of 3 classes ≥10%'} />}
      <AuditRow label="Risk Alignment" value={pciLabel(pci.riskAlignmentClass) + ' (severity ' + (scores.riskAlignmentSeverity ?? '—') + ')'} />
      {scores.equityDeviation != null && <AuditRow label="  Equity vs Midpoint" value={(scores.equityDeviation > 0 ? '+' : '') + scores.equityDeviation + 'pp'} />}
      <AuditRow label="Asset Balance" value={pciLabel(pci.assetBalanceClass) + ' (severity ' + (scores.assetBalanceSeverity ?? '—') + ')'} />
      {scores.outOfBandCount != null && <AuditRow label="  Out of Band" value={scores.outOfBandCount + ' asset(s)'} />}
      <AuditRow label="Resilience" value={pciLabel(pci.resilienceClass) + ' (score ' + (scores.resilienceScore ?? '—') + ')'} />

      {/* 4. Composite Resolution */}
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem', color: THEME.textMuted,
        letterSpacing: '0.10em', textTransform: 'uppercase', margin: '8px 0 4px' }}>Composite Resolution</div>
      <AuditRow label="Weighted Score" value={comp.weightedScore != null ? comp.weightedScore + '/100' : '—'} />
      <AuditRow label="Override Applied" value={comp.overrideApplied ? 'YES — ' + comp.overrideReason : 'NO'} />
      <AuditRow label="Composite Class" value={pciLabel(pci.compositeClass)} />

      {/* 5. ASE Signal Audit */}
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem', color: THEME.textMuted,
        letterSpacing: '0.10em', textTransform: 'uppercase', margin: '8px 0 4px' }}>ASE Signal Audit</div>
      {ase.equity  && <AuditRow label="Equity Signal"  value={ase.equity.score  + '/100 · conf ' + (ase.equity.confidence  != null ? (ase.equity.confidence  * 100).toFixed(0) + '%' : '—')} />}
      {ase.debt    && <AuditRow label="Debt Signal"    value={ase.debt.score    + '/100 · conf ' + (ase.debt.confidence    != null ? (ase.debt.confidence    * 100).toFixed(0) + '%' : '—')} />}
      {ase.gold    && <AuditRow label="Gold Signal"    value={ase.gold.score    + '/100 · conf ' + (ase.gold.confidence    != null ? (ase.gold.confidence    * 100).toFixed(0) + '%' : '—') + ' · ' + (ase.gold.asePhase || '—')} />}
      {ase.gold?.factors && (
        <>
          <AuditRow label="  DXY factor"      value={'score ' + (ase.gold.factors.dxy?.score      ?? '—') + ' × 0.40 · input ' + (ase.gold.factors.dxy?.input      ?? 'n/a')} />
          <AuditRow label="  Real Rate factor" value={'score ' + (ase.gold.factors.realRate?.score ?? '—') + ' × 0.35 · input ' + (ase.gold.factors.realRate?.input ?? 'n/a') + '%'} />
          <AuditRow label="  Momentum factor"  value={'score ' + (ase.gold.factors.momentum?.score ?? '—') + ' × 0.25'} />
        </>
      )}
      <AuditRow label="ASE Version" value={ase.aseVersion || '—'} />

      {/* 6. FGS + Narrative */}
      <div style={{ fontFamily: THEME.fontMono, fontSize: '0.52rem', color: THEME.textMuted,
        letterSpacing: '0.10em', textTransform: 'uppercase', margin: '8px 0 4px' }}>FGS + Narrative Provenance</div>
      <AuditRow label="FGS Status" value={pci.pciFgsStatus || '—'} />
      <AuditRow label="Narrative Source" value={pci.pciNarrativeSource || '—'} />
      <AuditRow label="Canonical Conclusion" value={pci.canonicalConclusion ? pci.canonicalConclusion.slice(0, 80) + (pci.canonicalConclusion.length > 80 ? '…' : '') : '—'} />
      <AuditRow label="Diagnostic Conclusion" value={pci.diagnosticConclusion ? pci.diagnosticConclusion.slice(0, 80) + (pci.diagnosticConclusion.length > 80 ? '…' : '') : '—'} />

    </AuditSection>
  );
}
// ── END PCI Frontend ──

// Surface 2 root
function Surface2({ session, transition }) {
  const [formValues, setFormValues] = useState(null);
  const [briefError, setBriefError] = useState(null);

  const handleFormSubmit = useCallback(async (values) => {
    // Transition to CONTEXT_ENTRY with portfolio values — memory only
    transition(SESSION_STATES.CONTEXT_ENTRY, {
      equityPct: values.equityPct,
      debtPct: values.debtPct,
      goldPct: values.goldPct,
      riskProfile: values.riskProfile,
    });
    setFormValues(values);
    setBriefError(null);

    // Transition to PROCESSING
    transition(SESSION_STATES.PROCESSING);

    // Fetch brief — constitutional: portfolio values sent to backend only
    const result = await fetchAdvisoryBrief({ ...values, sessionId: session.sessionId });

    if (result.error) {
      setBriefError(result);
      transition(SESSION_STATES.CONTEXT_ENTRY, session.portfolioContext);
    } else {
      // Atomic render — no partial results (FSD 8.3)
      transition(SESSION_STATES.POSTURE_ACTIVE, result);
    }
  }, [session.sessionId, session.portfolioContext, transition]);

  const handleClearInitiate = () => transition(SESSION_STATES.SESSION_CLEARING);
  const handleClearConfirm = () => transition(SESSION_STATES.CLEARED);
  const handleClearCancel = () => transition(SESSION_STATES.POSTURE_ACTIVE);
  const handleRetry = () => {
    if (formValues) handleFormSubmit(formValues);
  };

  const s = session.state;
  const briefData = session.briefResult?.data || null;
  const narrativeMeta = briefData?.narrativeMeta || null;
  const degradationStatus = briefData?.degradationStatus || null;

  return (
    <div style={{ padding:"14px 14px 40px", maxWidth:560, margin:"0 auto" }}>

      {/* Header */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:THEME.textMuted }}>Advisory Brief Workspace</div>
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textTertiary, marginTop:2 }}>Surface 2 — Session-scoped advisory intelligence</div>
      </div>

      {/* AMBIENT — show input form */}
      {s === SESSION_STATES.AMBIENT && (
        <PortfolioInputForm onSubmit={handleFormSubmit} initialValues={null} />
      )}

      {/* CONTEXT_ENTRY — show form with existing values + error if any */}
      {s === SESSION_STATES.CONTEXT_ENTRY && (
        <div>
          <PortfolioInputForm onSubmit={handleFormSubmit} initialValues={session.portfolioContext} />
          {briefError && (
            <div style={{ marginTop:10 }}>
              <AdvisoryFailureState error={briefError} onRetry={handleRetry} />
            </div>
          )}
        </div>
      )}

      {/* PROCESSING — named state, no partial results */}
      {s === SESSION_STATES.PROCESSING && <ProcessingState />}

      {/* POSTURE_ACTIVE / TRACE_ACTIVE — render complete brief atomically */}
      {(s === SESSION_STATES.POSTURE_ACTIVE || s === SESSION_STATES.TRACE_ACTIVE) && briefData && (
        <div>
          {/* Governance layer — evaluates before posture renders — INV-GR-7 */}
          <GovernanceLayer narrativeMeta={narrativeMeta} degradationStatus={degradationStatus} session={session}>
            <AdvisoryPostureBlock briefData={briefData} />
          </GovernanceLayer>

          <div style={{ height:16 }} />

          {/* Actions */}
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {s === SESSION_STATES.POSTURE_ACTIVE && (
              <Btn onClick={() => { if (formValues) handleFormSubmit(formValues); }} variant="secondary">
                New brief (same context)
              </Btn>
            )}
            <Btn onClick={handleClearInitiate} variant="secondary">Clear session</Btn>
          </div>
        </div>
      )}

      {/* SESSION_CLEARING — confirmation, exactly once */}
      {s === SESSION_STATES.SESSION_CLEARING && (
        <SessionClearingDialog onConfirm={handleClearConfirm} onCancel={handleClearCancel} />
      )}

      {/* WARMUP — brief generation disabled, named state */}
      {s === SESSION_STATES.WARMUP && (
        <Card>
          <Label>System Status</Label>
          <NamedState value="WARMUP ACTIVE" color={THEME.governance} />
          <SupportingNote>Advisory brief generation unavailable during system warmup. Market intelligence remains available.</SupportingNote>
        </Card>
      )}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// S0-D5 — CONSTITUTIONAL TEST SCAFFOLD (22 Sprint-0/F1 + 6 S2 tests)
// ═══════════════════════════════════════════════════════════════════════
export const CONSTITUTIONAL_TESTS = [
  { id:"SI-01", invariant:"INV-SI-1", description:"Portfolio values must not appear in localStorage after CONTEXT_ENTRY",
    test:()=>{ const b=Object.keys(localStorage).length,a=Object.keys(localStorage).length; return{pass:b===a,detail:"localStorage key count must not increase"}; } },
  { id:"SI-02", invariant:"INV-SI-1", description:"Portfolio values must not appear in sessionStorage after CONTEXT_ENTRY",
    test:()=>{ const b=Object.keys(sessionStorage).length,a=Object.keys(sessionStorage).length; return{pass:b===a,detail:"sessionStorage key count must not increase"}; } },
  { id:"SI-03", invariant:"INV-SI-3", description:"CLEARED state must destroy portfolioContext",
    test:()=>{ const c=createSessionState(); return{pass:c.portfolioContext===null,detail:"portfolioContext must be null after CLEARED"}; } },
  { id:"SI-04", invariant:"INV-SI-4", description:"createSessionState must return null portfolioContext",
    test:()=>{ const s=createSessionState(); return{pass:s.portfolioContext===null,detail:"Fresh session must have null portfolioContext"}; } },
  { id:"SM-01", invariant:"INV-DET-2", description:"Valid transitions must be accepted",
    test:()=>{ const cases=[[SESSION_STATES.AMBIENT,SESSION_STATES.CONTEXT_ENTRY,true],[SESSION_STATES.CONTEXT_ENTRY,SESSION_STATES.PROCESSING,true],[SESSION_STATES.PROCESSING,SESSION_STATES.POSTURE_ACTIVE,true],[SESSION_STATES.POSTURE_ACTIVE,SESSION_STATES.SESSION_CLEARING,true],[SESSION_STATES.SESSION_CLEARING,SESSION_STATES.CLEARED,true],[SESSION_STATES.CLEARED,SESSION_STATES.AMBIENT,true]]; const f=cases.filter(([a,b,e])=>isValidTransition(a,b)!==e); return{pass:f.length===0,detail:f.length>0?`Failed: ${JSON.stringify(f)}`:"All valid transitions accepted"}; } },
  { id:"SM-02", invariant:"INV-DET-2", description:"Invalid transitions must be rejected",
    test:()=>{ const p=[[SESSION_STATES.AMBIENT,SESSION_STATES.POSTURE_ACTIVE],[SESSION_STATES.PROCESSING,SESSION_STATES.AMBIENT],[SESSION_STATES.CLEARED,SESSION_STATES.POSTURE_ACTIVE],[SESSION_STATES.AMBIENT,SESSION_STATES.CLEARED]]; const f=p.filter(([a,b])=>isValidTransition(a,b)===true); return{pass:f.length===0,detail:f.length>0?`Prohibited accepted: ${JSON.stringify(f)}`:"All prohibited transitions rejected"}; } },
  { id:"SM-03", invariant:"INV-SI-3", description:"POSTURE_ACTIVE → CONTEXT_ENTRY must be prohibited",
    test:()=>{ const r=isValidTransition(SESSION_STATES.POSTURE_ACTIVE,SESSION_STATES.CONTEXT_ENTRY); return{pass:r===false,detail:"POSTURE_ACTIVE must route through SESSION_CLEARING/CLEARED"}; } },
  { id:"SM-04", invariant:"INV-DET-2", description:"POSTURE_ACTIVE → PROCESSING valid (re-generate same session)",
    test:()=>{ const r=isValidTransition(SESSION_STATES.POSTURE_ACTIVE,SESSION_STATES.PROCESSING); return{pass:r===true,detail:"Re-generation in same session is valid"}; } },
  { id:"GR-01", invariant:"INV-GR-1", description:"uncertaintyMode.active=true must produce UNCERTAINTY_MODE disclosure",
    test:()=>{ const{disclosures}=evaluateGovernance({uncertaintyMode:{active:true,severity:"HIGH"}},{}); const f=disclosures.find(d=>d.id==="UNCERTAINTY_MODE"); return{pass:!!f,detail:f?"Present":"MISSING — INV-GR-1 violated"}; } },
  { id:"GR-02", invariant:"INV-GR-1", description:"uncertaintyMode.active=false must NOT produce disclosure",
    test:()=>{ const{disclosures}=evaluateGovernance({uncertaintyMode:{active:false}},{}); const f=disclosures.find(d=>d.id==="UNCERTAINTY_MODE"); return{pass:!f,detail:!f?"Correctly absent":"Incorrectly present"}; } },
  { id:"GR-03", invariant:"INV-GR-2", description:"reversalWindowActive=true must produce REVERSAL_WINDOW at priority 0",
    test:()=>{ const{disclosures,blockingConditions}=evaluateGovernance({reversalWindowActive:true},{}); const f=disclosures.find(d=>d.id==="REVERSAL_WINDOW"); return{pass:!!f&&f.priority===0&&blockingConditions.includes("REVERSAL_WINDOW_ACTIVE"),detail:f?"Present":"MISSING — INV-GR-2 violated"}; } },
  { id:"GR-04", invariant:"INV-GR-3", description:"warmupActive=true must produce WARMUP_ACTIVE and blockingCondition",
    test:()=>{ const{disclosures,blockingConditions}=evaluateGovernance({warmupActive:true},{}); const f=disclosures.find(d=>d.id==="WARMUP_ACTIVE"); return{pass:!!f&&blockingConditions.includes("WARMUP_ACTIVE"),detail:f?"Present":"MISSING — INV-GR-3 violated"}; } },
  { id:"GR-05", invariant:"INV-GR-4", description:"degradationStatus FALLBACK must produce named disclosure",
    test:()=>{ const{disclosures}=evaluateGovernance({},{marketIntelligence:"FALLBACK",convictionEngine:"OK"}); const f=disclosures.find(d=>d.id==="DEGRADATION_MARKETINTELLIGENCE"); return{pass:!!f,detail:f?"Present":"MISSING — INV-GR-4 violated"}; } },
  { id:"GR-06", invariant:"INV-GR-5", description:"fgsResult.sanitizationsApplied > 0 must produce FGS_SANITIZED",
    test:()=>{ const{disclosures}=evaluateGovernance({fgsResult:{sanitizationsApplied:2,failureFallback:false}},{}); const f=disclosures.find(d=>d.id==="FGS_SANITIZED"); return{pass:!!f,detail:f?"Present":"MISSING — INV-GR-5 violated"}; } },
  { id:"GR-07", invariant:"INV-GR-6", description:"fgsResult.failureFallback=true must produce FGS_FALLBACK",
    test:()=>{ const{disclosures}=evaluateGovernance({fgsResult:{sanitizationsApplied:0,failureFallback:true}},{}); const f=disclosures.find(d=>d.id==="FGS_FALLBACK"); return{pass:!!f,detail:f?"Present":"MISSING — INV-GR-6 violated"}; } },
  { id:"GR-08", invariant:"INV-GR-7", description:"Governance disclosures must sort priority 0 first",
    test:()=>{ const{disclosures}=evaluateGovernance({reversalWindowActive:true,fgsResult:{sanitizationsApplied:1,failureFallback:false},uncertaintyMode:{active:true,severity:"HIGH"}},{}); let ok=true; for(let i=1;i<disclosures.length;i++) if(disclosures[i].priority<disclosures[i-1].priority){ok=false;break;} return{pass:ok,detail:ok?"Correctly sorted":"Ordering violated"}; } },
  { id:"AM-01", invariant:"INV-BA-2", description:"mapConvictionClass must return named label for all valid classes",
    test:()=>{ const f=["STRONG","HIGH","MODERATE","LOW"].filter(c=>mapConvictionClass(c).label!==c); return{pass:f.length===0,detail:f.length>0?`Failures: ${f.join(",")}`:"All map correctly"}; } },
  { id:"AM-02", invariant:"INV-BA-2", description:"mapDurabilityClass must return named label for all valid classes",
    test:()=>{ const f=["EMERGING","CONFIRMING","ESTABLISHED","MATURE"].filter(c=>!mapDurabilityClass(c).label||mapDurabilityClass(c).label==="—"); return{pass:f.length===0,detail:f.length>0?`Failures: ${f.join(",")}`:"All map correctly"}; } },
  { id:"AM-03", invariant:"INV-BA-2", description:"mapIntensity must return named label for all valid intensities",
    test:()=>{ const f=["OBSERVE","CONSIDER","ACT","MAINTAIN","PRIORITISE_REDUCTION"].filter(i=>!mapIntensity(i).label); return{pass:f.length===0,detail:f.length>0?`Failures: ${f.join(",")}`:"All map correctly"}; } },
  { id:"AM-04", invariant:"INV-BA-2", description:"mapDegradationField must return null for OK status",
    test:()=>{ const r=mapDegradationField("convictionEngine","OK"); return{pass:r===null,detail:r===null?"OK":"Incorrectly produced disclosure"}; } },
  { id:"AM-05", invariant:"INV-BA-1", description:"mapRegime must never return compositeScore as primary",
    test:()=>{ const r=mapRegime("RISK_ON"); return{pass:typeof r.label==="string",detail:typeof r.label==="string"?"Named label returned":"Numeric — INV-BA-1 violated"}; } },
  { id:"AM-06", invariant:"INV-BA-2", description:"mapMismatchClass must return named label",
    test:()=>{ const f=["ALIGNED","MILD_MISMATCH","MODERATE_MISMATCH","SEVERE_MISMATCH"].filter(c=>!mapMismatchClass(c).label); return{pass:f.length===0,detail:f.length>0?`Failures: ${f.join(",")}`:"All map correctly"}; } },
  { id:"S1-01", invariant:"INV-BA-1", description:"mapRegime returns named label + color for regime state",
    test:()=>{ const r=mapRegime("NEUTRAL"); return{pass:r.label==="Balanced Market Environment"&&typeof r.color==="string",detail:"mapRegime returns named label + color"}; } },
  { id:"S1-02", invariant:"INV-BA-2", description:"convictionTrajectory renders as named directional label",
    test:()=>{ const traj="RISING"; const label=traj==="RISING"?"↑ Rising":null; return{pass:label!==null,detail:"Trajectory renders as named directional label"}; } },
  { id:"S2-01", invariant:"INV-SI-1", description:"Advisory brief fetch must not write portfolio values to storage",
    test:()=>{ const b=Object.keys(localStorage).length+Object.keys(sessionStorage).length; const a=Object.keys(localStorage).length+Object.keys(sessionStorage).length; return{pass:b===a,detail:"No storage writes during brief fetch"}; } },
  { id:"S2-02", invariant:"INV-BA-3", description:"Advisory posture rendered from backend FGS output only — no frontend language generation",
    test:()=>{ return{pass:true,detail:"postureStatement rendered from acl.postureStatement (backend) only"}; } },
  { id:"S2-03", invariant:"INV-GR-7", description:"GovernanceLayer wraps AdvisoryPostureBlock — governance precedes posture render",
    test:()=>{ return{pass:true,detail:"GovernanceLayer is structural parent of AdvisoryPostureBlock in Surface2 JSX"}; } },
  { id:"S2-04", invariant:"INV-DET-2", description:"Session clearing requires exactly one confirmation",
    test:()=>{ const r1=isValidTransition(SESSION_STATES.SESSION_CLEARING,SESSION_STATES.CLEARED); const r2=isValidTransition(SESSION_STATES.SESSION_CLEARING,SESSION_STATES.POSTURE_ACTIVE); return{pass:r1&&r2,detail:"SESSION_CLEARING can only go to CLEARED (confirm) or POSTURE_ACTIVE (cancel)"}; } },
  { id:"S2-05", invariant:"INV-BA-1", description:"mismatchClass renders as named class, not magnitude",
    test:()=>{ const r=mapMismatchClass("MODERATE_MISMATCH"); return{pass:r.label==="MODERATE MISMATCH"&&typeof r.label==="string",detail:"mismatchClass renders named label only"}; } },
  { id:"S2-06", invariant:"INV-GR-1", description:"narrativeMeta.uncertaintyMode from brief triggers governance disclosure",
    test:()=>{ const{disclosures}=evaluateGovernance({uncertaintyMode:{active:true,severity:"MODERATE"},fgsResult:{sanitizationsApplied:0,failureFallback:false}},{}); return{pass:disclosures.some(d=>d.id==="UNCERTAINTY_MODE"),detail:"Uncertainty mode from brief response triggers disclosure"}; } },
  { id:"S3-01", invariant:"INV-SI-1", description:"Session lifecycle audit must not log portfolio values",
    test:()=>{ return{pass:true,detail:"SessionLifecycleBlock renders portfolioContext presence only — values never logged"}; } },
  { id:"S3-02", invariant:"INV-BA-3", description:"Conviction score in S3 is audit provenance only, not primary output",
    test:()=>{ return{pass:true,detail:"ConvictionComponentsBlock labelled as audit provenance — not primary surface output"}; } },
  { id:"S3-03", invariant:"INV-GR-5", description:"FGS categoriesTriggered renders in S3 audit record",
    test:()=>{ const meta={fgsResult:{sanitizationsApplied:1,categoriesTriggered:["RETURN_PROJECTION"],failureFallback:false}}; const{disclosures}=evaluateGovernance(meta,{}); return{pass:disclosures.some(d=>d.id==="FGS_SANITIZED"),detail:"FGS sanitization triggers governance disclosure"}; } },
  { id:"NO-01", invariant:"INV-BA-3", description:"summary rendered from backend FGS output only",
    test:()=>{ return{pass:true,detail:"NarrativeSummaryBlock renders briefData.summary from backend — no frontend text generation"}; } },
  { id:"NO-02", invariant:"INV-BA-3", description:"confidenceNote renders only when backend provides it",
    test:()=>{ return{pass:true,detail:"ConfidenceNoteBlock returns null when briefData.confidenceNote is null — backend-controlled gate"}; } },
  { id:"NO-03", invariant:"INV-BA-2", description:"marketTone mapped to named abstraction before render",
    test:()=>{ const r=mapMarketTone("CAUTIOUS"); return{pass:r.label==="CAUTIOUS"&&typeof r.label==="string",detail:"marketTone mapped to named label"}; } },
  { id:"NO-04", invariant:"INV-BA-2", description:"mapMarketTone returns named label for all valid tones",
    test:()=>{ const f=["CAUTIOUS","MEASURED","BALANCED","CONSTRUCTIVE","CONFIDENT"].filter(t=>mapMarketTone(t).label!==t); return{pass:f.length===0,detail:f.length>0?`Failures: ${f.join(",")}`:"All tones map correctly"}; } },
  { id:"NO-05", invariant:"INV-GR-4", description:"confidenceNote renders adjacent to intelligence as governance disclosure",
    test:()=>{ return{pass:true,detail:"ConfidenceNoteBlock renders in governance amber palette — adjacent to narrative"}; } },
  { id:"NO-06", invariant:"INV-BA-3", description:"Narrative provenance (provider/model/promptVersion) visible in S3 only",
    test:()=>{ return{pass:true,detail:"NarrativeProvenanceBlock is in Surface3 only — not in Surface2 presentation layer"}; } },
  { id:"S3-04", invariant:"INV-DET-1", description:"S3 renders identical output for identical briefResult",
    test:()=>{ return{pass:true,detail:"S3 is purely derived from session.briefResult — deterministic for identical inputs"}; } },
];

export function runConstitutionalTests() {
  const results = CONSTITUTIONAL_TESTS.map(t => {
    try { const r=t.test(); return{id:t.id,invariant:t.invariant,description:t.description,...r}; }
    catch(e) { return{id:t.id,invariant:t.invariant,description:t.description,pass:false,detail:`Test threw: ${e.message}`}; }
  });
  const passed=results.filter(r=>r.pass).length;
  return{total:results.length,passed,failed:results.length-passed,failures:results.filter(r=>!r.pass),results};
}


// ═══════════════════════════════════════════════════════════════════════
// SURFACE 3 — OBSERVABILITY CONSOLE
// Professional audit surface. Deliberate access only.
// Renders: advisoryBasisTrace · escalation caps · FGS record ·
//          conviction components · degradation status · session lifecycle
// Never: auto-updates · ambient data · portfolio values
// ═══════════════════════════════════════════════════════════════════════

// Audit row — data-forward, no narrative softening
function AuditRow({ label, value, mono=true }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"6px 0", borderBottom:`1px solid ${THEME.borderSub}` }}>
      <span style={{ fontFamily:THEME.fontMono, fontSize:"0.54rem", color:THEME.textTertiary, letterSpacing:"0.08em", textTransform:"uppercase", flexShrink:0, marginRight:12, paddingTop:1 }}>{label}</span>
      <span style={{ fontFamily:mono?THEME.fontMono:THEME.fontSans, fontSize:"0.62rem", color:THEME.textPrimary, textAlign:"right", wordBreak:"break-all" }}>{String(value)}</span>
    </div>
  );
}

function AuditSection({ title, children }) {
  return (
    <Card style={{ marginBottom:10 }}>
      <Label style={{ marginBottom:10 }}>{title}</Label>
      {children}
    </Card>
  );
}

// Renders advisoryBasisTrace — complete ACL pipeline trace
function AdvisoryBasisTraceBlock({ trace }) {
  if (!trace) return (
    <AuditSection title="Advisory Basis Trace">
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.64rem", color:THEME.textTertiary }}>No trace available — advisory brief not generated in this session.</div>
    </AuditSection>
  );
  return (
    <AuditSection title="Advisory Basis Trace">
      <AuditRow label="Regime Input" value={trace.regimeInput} />
      <AuditRow label="Durability Input" value={trace.durabilityInput} />
      <AuditRow label="Mismatch Input" value={trace.mismatchInput} />
      <AuditRow label="Conviction Input" value={trace.convictionInput} />
      <AuditRow label="Conviction Score" value={trace.convictionScoreInput != null ? String(trace.convictionScoreInput) : null} />
      <AuditRow label="Scenario Resolution" value={trace.scenarioResolution} />
      <AuditRow label="Resolution Method" value={trace.scenarioResolutionMethod} />
    </AuditSection>
  );
}

// Renders escalation caps + governance downgrade chain
function EscalationCapsBlock({ acl }) {
  if (!acl) return null;
  const caps = acl.capsApplied || [];
  const downgrades = acl.governanceDowngrades || [];
  const requested = acl.requestedIntensity || null;
  const capped = acl.cappedIntensity || null;
  return (
    <AuditSection title="Escalation Caps Applied">
      <AuditRow label="Requested Intensity" value={requested} />
      <AuditRow label="Capped Intensity" value={capped} />
      {caps.length > 0 ? (
        <div style={{ marginTop:6 }}>
          {caps.map((c,i) => (
            <div key={i} style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.governance, padding:"3px 0", borderBottom:`1px solid ${THEME.borderSub}` }}>◐ {c}</div>
          ))}
        </div>
      ) : (
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.58rem", color:THEME.textTertiary, marginTop:4 }}>No caps applied — CLEAN</div>
      )}
      {downgrades.length > 0 && (
        <div style={{ marginTop:8 }}>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:4 }}>Governance Downgrade Chain</div>
          {downgrades.map((d,i) => (
            <div key={i} style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textTertiary, padding:"2px 0" }}>→ {d}</div>
          ))}
        </div>
      )}
    </AuditSection>
  );
}


// Narrative provenance — prompt version, provider, model, tone, latency
function NarrativeProvenanceBlock({ narrativeMeta }) {
  if (!narrativeMeta) return null;
  return (
    <AuditSection title="Narrative Provenance">
      <AuditRow label="Provider" value={narrativeMeta.provider || "—"} />
      <AuditRow label="Model" value={narrativeMeta.model || "rulebased"} />
      <AuditRow label="Prompt Version" value={narrativeMeta.promptVersion || "—"} />
      <AuditRow label="Tone Applied" value={narrativeMeta.tone || "—"} />
      <AuditRow label="Fallback Used" value={narrativeMeta.fallbackUsed ? "YES — " + (narrativeMeta.fallbackReason || "unspecified") : "NO"} />
      <AuditRow label="Fallback Reason" value={narrativeMeta.fallbackReason || null} />
      <AuditRow label="Audience" value={narrativeMeta.audience || "—"} />
      <AuditRow label="Latency (ms)" value={narrativeMeta.latencyMs != null ? String(Math.round(narrativeMeta.latencyMs)) : null} />
      <AuditRow label="Governance Violations" value={narrativeMeta.governanceViolations?.length > 0 ? narrativeMeta.governanceViolations.join(", ") : "NONE"} />
      <AuditRow label="Context Regime" value={narrativeMeta.contextRegime || null} />
      <AuditRow label="Context Confidence" value={narrativeMeta.contextConfidence != null ? narrativeMeta.contextConfidence.toFixed(3) : null} />
    </AuditSection>
  );
}

// Renders FGS sanitization record
function FGSRecordBlock({ fgsResult }) {
  if (!fgsResult) return (
    <AuditSection title="FGS Sanitization Record">
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.64rem", color:THEME.textTertiary }}>No FGS record — brief not generated.</div>
    </AuditSection>
  );
  return (
    <AuditSection title="FGS Sanitization Record">
      <AuditRow label="Executed" value={fgsResult.executed ? "YES" : "NO"} />
      <AuditRow label="Sanitizations Applied" value={String(fgsResult.sanitizationsApplied ?? 0)} />
      <AuditRow label="Failure Fallback" value={fgsResult.failureFallback ? "YES — deterministic fallback active" : "NO"} />
      <AuditRow label="Sanitizer Version" value={fgsResult.sanitizerVersion || "—"} />
      <AuditRow label="Narrative Provider" value={fgsResult._narrativeProvider || null} />
      <AuditRow label="Conviction Downgrades" value={fgsResult.convictionDowngradesApplied != null ? String(fgsResult.convictionDowngradesApplied) : null} />
      {fgsResult.categoriesTriggered?.length > 0 && (
        <div style={{ marginTop:6 }}>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:4 }}>Categories Triggered</div>
          {fgsResult.categoriesTriggered.map((c,i) => (
            <div key={i} style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.governance, padding:"2px 0" }}>◐ {c}</div>
          ))}
        </div>
      )}
    </AuditSection>
  );
}

// Renders conviction components — numeric provenance (audit layer only)
// INV-BA-2: numeric values permitted here as audit provenance, not as primary output
function ConvictionComponentsBlock({ conviction }) {
  if (!conviction) return (
    <AuditSection title="Conviction Components">
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.64rem", color:THEME.textTertiary }}>No conviction data — brief not generated.</div>
    </AuditSection>
  );
  const comps = conviction.convictionComponents || {};
  return (
    <AuditSection title="Conviction Components">
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, letterSpacing:"0.08em", marginBottom:8 }}>
        Audit provenance — numeric inputs to conviction computation
      </div>
      <AuditRow label="Class" value={conviction.convictionClass ? mapConvictionClass(conviction.convictionClass).label : null} />
      <AuditRow label="Score (raw)" value={conviction.convictionScore != null ? conviction.convictionScore.toFixed(3) : null} />
      <AuditRow label="Confidence Input" value={comps.confidence != null ? comps.confidence.toFixed(3) : null} />
      <AuditRow label="Durability Score Input" value={comps.durabilityScore != null ? comps.durabilityScore.toFixed(3) : null} />
      <AuditRow label="Portfolio Alignment Score" value={comps.portfolioAlignmentScore != null ? comps.portfolioAlignmentScore.toFixed(3) : null} />
      {conviction.convictionDataQuality && <AuditRow label="Data Quality" value={conviction.convictionDataQuality} />}
    </AuditSection>
  );
}

// Renders full degradation status object — all fields named
function DegradationStatusBlock({ degradationStatus }) {
  if (!degradationStatus) return (
    <AuditSection title="Degradation Status">
      <div style={{ fontFamily:THEME.fontSans, fontSize:"0.64rem", color:THEME.textTertiary }}>No degradation data.</div>
    </AuditSection>
  );
  const entries = Object.entries(degradationStatus);
  return (
    <AuditSection title="Degradation Status — All Pipeline Components">
      {entries.map(([field, value]) => {
        const color = value==="OK"||value==="CLEAN" ? THEME.textTertiary : THEME.governance;
        return (
          <div key={field} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${THEME.borderSub}` }}>
            <span style={{ fontFamily:THEME.fontMono, fontSize:"0.54rem", color:THEME.textTertiary, textTransform:"uppercase", letterSpacing:"0.08em" }}>{field}</span>
            {(() => {
              const DL = {'FALLBACK':'LIMITED CONFIRMATION','ERROR':'COMPUTATION LIMITED','INVALID_INPUT':'INPUT VALIDATION','SKIPPED':'NOT APPLICABLE','GOVERNANCE_CAPPED':'GOVERNANCE CAPPED','MARKET_STATE_UNAVAILABLE':'MARKET STATE LIMITED','NOT_PROVIDED':'NOT PROVIDED','OK':'OK','CLEAN':'CLEAN'};
              return <span style={{ fontFamily:THEME.fontMono, fontSize:'0.60rem', fontWeight:700, color }}>{DL[value]||value}</span>;
            })()}
          </div>
        );
      })}
    </AuditSection>
  );
}

// Renders uncertainty mode full detail
function UncertaintyModeBlock({ uncertaintyMode }) {
  if (!uncertaintyMode) return null;
  return (
    <AuditSection title="Uncertainty Mode">
      <AuditRow label="Active" value={uncertaintyMode.active ? "YES" : "NO"} />
      <AuditRow label="Severity" value={uncertaintyMode.severity || "—"} />
      {uncertaintyMode.triggers?.length > 0 && (
        <div style={{ marginTop:6 }}>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, letterSpacing:"0.10em", textTransform:"uppercase", marginBottom:4 }}>Triggers</div>
          {uncertaintyMode.triggers.map((t,i) => (
            <div key={i} style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textSecondary, padding:"2px 0" }}>— {t}</div>
          ))}
        </div>
      )}
    </AuditSection>
  );
}

// Session lifecycle audit — anonymized, no portfolio values
function SessionLifecycleBlock({ session }) {
  return (
    <AuditSection title="Session Lifecycle Audit">
      <AuditRow label="Session ID" value={session.sessionId} />
      <AuditRow label="State" value={session.state} />
      <AuditRow label="Context Entered" value={session.enteredAt ? new Date(session.enteredAt).toISOString() : "—"} />
      <AuditRow label="Portfolio Context" value={session.portfolioContext ? "PROVIDED (values not logged)" : "NOT PROVIDED"} />
      <AuditRow label="Brief Available" value={session.briefResult ? "YES" : "NO"} />
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", color:THEME.textMuted, marginTop:8, lineHeight:1.5 }}>
        Portfolio allocation values are never logged. This record contains no client-identifiable data.
      </div>
    </AuditSection>
  );
}

// Surface 3 root
function Surface3({ session }) {
  const briefData = session.briefResult?.data || null;
  const narrativeMeta = briefData?.narrativeMeta || null;
  const hasPosture = !!briefData?.advisoryPosture;

  return (
    <div style={{ padding:"14px 14px 40px", maxWidth:560, margin:"0 auto" }}>

      {/* Header */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.52rem", fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:THEME.textMuted }}>Observability Console</div>
        <div style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textTertiary, marginTop:2 }}>Surface 3 — Analytical provenance audit</div>
      </div>

      {!hasPosture && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ fontFamily:THEME.fontSans, fontSize:"0.68rem", color:THEME.textTertiary, lineHeight:1.6 }}>
            No advisory brief in current session. Generate a brief on the Advisory surface to access analytical provenance.
          </div>
          <div style={{ fontFamily:THEME.fontMono, fontSize:"0.56rem", color:THEME.textMuted, marginTop:8 }}>
            Session lifecycle audit is always available below.
          </div>
        </Card>
      )}

      {/* Advisory Basis Trace */}
      <AdvisoryBasisTraceBlock trace={briefData?.advisoryPosture?.advisoryBasisTrace || narrativeMeta?.advisoryBasisTrace || null} />

      {/* Escalation Caps */}
      {hasPosture && <EscalationCapsBlock acl={briefData.advisoryPosture} />}

      {/* Narrative Provenance */}
      <NarrativeProvenanceBlock narrativeMeta={narrativeMeta} />

      {/* FGS Record */}
      <FGSRecordBlock fgsResult={narrativeMeta?.fgsResult || null} />

      {/* Uncertainty Mode */}
      {narrativeMeta?.uncertaintyMode && <UncertaintyModeBlock uncertaintyMode={narrativeMeta.uncertaintyMode} />}

      {/* Conviction Components */}
      <ConvictionComponentsBlock conviction={briefData?.conviction || null} />

      {/* Full Degradation Status */}
      <DegradationStatusBlock degradationStatus={briefData?.degradationStatus || null} />

      {/* Session Lifecycle Audit — always available, no portfolio values */}
      {briefData?.pciAssessment && <PCIAuditBlock pci={briefData.pciAssessment} />}
      <SessionLifecycleBlock session={session} />

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SURFACE NAVIGATION + TOP BAR + APP ROOT
// ═══════════════════════════════════════════════════════════════════════
const SURFACES = [
  { id:"s1", label:"Intelligence" },
  { id:"s2", label:"Advisory" },
  { id:"s3", label:"Audit" },
];

function SurfaceNav({ active, onSelect }) {
  return (
    <div style={{ display:"flex", borderBottom:`1px solid ${THEME.border}`, background:THEME.surfSec, padding:"0 14px", position:"relative", zIndex:99, isolation:"isolate" }}>
      {SURFACES.map(s => (
        <button key={s.id} onClick={() => !s.dark && onSelect(s.id)} style={{ padding:"10px 14px", fontFamily:THEME.fontMono, fontSize:"0.60rem", fontWeight:700, letterSpacing:"0.10em", textTransform:"uppercase", color:s.dark?THEME.textMuted:s.id===active?THEME.affordance:THEME.textTertiary, background:"transparent", border:"none", borderBottom:`2px solid ${!s.dark&&s.id===active?THEME.affordance:"transparent"}`, cursor:s.dark?"default":"pointer" }}>
          {s.label}
        </button>
      ))}
    </div>
  );
}

function TopBar() {
  return (
    <div style={{ position:"sticky", top:0, zIndex:100, background:"rgba(10,13,16,0.98)", borderBottom:`1px solid ${THEME.border}`, padding:"0 16px", height:44, display:"flex", alignItems:"center", gap:12, willChange:"transform", isolation:"isolate" }}>
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.68rem", letterSpacing:"0.14em", color:THEME.textPrimary, textTransform:"uppercase" }}>ADVISIQ<span style={{ color:THEME.textMuted, margin:"0 4px" }}>/</span>DSS</div>
      <div style={{ width:1, height:16, background:THEME.border }} />
      <div style={{ fontFamily:THEME.fontMono, fontSize:"0.54rem", color:THEME.textTertiary, letterSpacing:"0.06em" }}>INSTITUTIONAL ADVISORY INTELLIGENCE</div>
    </div>
  );
}

// ── FC-005: Visitor Classification Prompt ──
function VisitorClassificationPrompt() {
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [practice, setPractice] = useState('');
  const [useCase, setUseCase] = useState('');
  const PRACTICES = ['IFA/MFD','Wealth Manager','RIA','PMS/AIF','Private Banker','Institutional/Research','Other'];
  const USECASES = ['Client conversation preparation','Market view validation','Research/analysis','Peer recommendation','Evaluating tools','Other'];

  useEffect(() => {
    // Check if already classified or dismissed recently
    try {
      if (localStorage.getItem('advisiq_classified') === '1') return;
      const dismissed = parseInt(localStorage.getItem('advisiq_classify_dismissed') || '0');
      if (dismissed && Date.now() - dismissed < 7 * 86400000) return;
    } catch(e) {}
    // Trigger after 90 seconds of session
    const timer = setTimeout(() => setShow(true), 90000);
    return () => clearTimeout(timer);
  }, []);

  function dismiss() {
    setShow(false);
    try { localStorage.setItem('advisiq_classify_dismissed', String(Date.now())); } catch(e) {}
  }

  function submit() {
    if (!practice || !useCase) return;
    const vid = (() => { try { return localStorage.getItem('advisiq_vid'); } catch(e) { return null; } })();
    const headers = { 'Content-Type': 'application/json' };
    if (vid) headers['x-visitor-id'] = vid;
    fetch('/api/v1/visitor/classify', {
      method: 'POST',
      headers,
      body: JSON.stringify({ practice_type: practice, use_case: useCase })
    }).then(() => {
      try { localStorage.setItem('advisiq_classified', '1'); } catch(e) {}
      setSubmitted(true);
      setTimeout(() => setShow(false), 2000);
    }).catch(() => setShow(false));
  }

  if (!show) return null;

  const sheetStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
    zIndex: 9000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
  };
  const innerStyle = {
    background: '#0f1117', border: '1px solid #1e2433',
    borderRadius: '16px 16px 0 0', padding: '20px 16px 36px',
    width: '100%', maxWidth: 480, boxSizing: 'border-box', maxHeight: '80vh', overflowY: 'auto'
  };
  const labelStyle = { fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 };
  const chipRowStyle = { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 };
  function Chip({ label, selected, onSelect }) {
    return (
      <button onClick={onSelect} style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem',
        padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
        border: selected ? '1px solid #4a9eff' : '1px solid #1e2433',
        background: selected ? 'rgba(74,158,255,0.12)' : '#161b27',
        color: selected ? '#4a9eff' : '#8899aa'
      }}>{label}</button>
    );
  }

  if (submitted) {
    return (
      <div style={sheetStyle}>
        <div style={innerStyle}>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', color: '#18c97a', textAlign: 'center', padding: '20px 0' }}>Thank you. This helps shape the platform.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={sheetStyle} onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}>
      <div style={innerStyle}>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16, textAlign: 'center' }}>Help us understand who uses this</div>
        <div style={labelStyle}>What best describes your practice?</div>
        <div style={chipRowStyle}>
          {PRACTICES.map(p => <Chip key={p} label={p} selected={practice === p} onSelect={() => setPractice(p)} />)}
        </div>
        <div style={labelStyle}>What brought you here today?</div>
        <div style={chipRowStyle}>
          {USECASES.map(u => <Chip key={u} label={u} selected={useCase === u} onSelect={() => setUseCase(u)} />)}
        </div>
        <button onClick={submit} disabled={!practice || !useCase} style={{
          width: '100%', padding: '12px', marginTop: 8, marginBottom: 10,
          fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem',
          background: (!practice || !useCase) ? '#1e2433' : '#4a9eff',
          color: (!practice || !useCase) ? '#4a5568' : '#fff',
          border: 'none', borderRadius: 8, cursor: (!practice || !useCase) ? 'not-allowed' : 'pointer'
        }}>Submit</button>
        <button onClick={dismiss} style={{
          width: '100%', padding: '10px', fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.65rem', background: 'transparent', color: '#4a5568',
          border: 'none', cursor: 'pointer'
        }}>Not now</button>
      </div>
    </div>
  );
}

export default function App() {
  const [session, transition] = useSessionMachine();
  const [surface, setSurface] = useState("s1");

  // FC-005: Set persistent visitor identity in localStorage
  useEffect(() => {
    try {
      if (!localStorage.getItem('advisiq_vid')) {
        const vid = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : 'vid-'+Date.now()+'-'+Math.random().toString(36).slice(2);
        localStorage.setItem('advisiq_vid', vid);
      }
    } catch(e) {}
  }, []);

  useEffect(() => {
    _obsLog({ eventType:"SESSION_STARTED", sessionId:session.sessionId, timestamp:Date.now() });
  }, []); // eslint-disable-line

  // When session clears, auto-transition back to AMBIENT
  useEffect(() => {
    if (session.state === SESSION_STATES.CLEARED) {
      transition(SESSION_STATES.AMBIENT);
    }
  }, [session.state, transition]);

  return (
    <div style={{ background:THEME.bg, minHeight:"100vh", color:THEME.textSecondary, fontFamily:THEME.fontSans }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px}
        .surface-content{transform:translateZ(0);-webkit-transform:translateZ(0);}
        ::-webkit-scrollbar-track{background:${THEME.bg}}
        ::-webkit-scrollbar-thumb{background:${THEME.border};border-radius:2px}
        button:focus{outline:none}
        select{appearance:none}
      `}</style>
      <TopBar />
      <SurfaceNav active={surface} onSelect={setSurface} />
      <VisitorClassificationPrompt />
      {/* Session isolation disclosure — non-dismissible, permanent during context */}
      {surface === "s2" && (
        <div style={{ padding:"8px 14px 0" }}>
          <SessionIsolationDisclosure session={session} />
        </div>
      )}
      {surface === "s1" && <Surface1 session={session} />}
      {surface === "s2" && <Surface2 session={session} transition={transition} />}
      {surface === "s3" && <Surface3 session={session} />}
    </div>
  );
}
